/**
 * Financeiro A2 — Contas a Pagar (mockups F01–F08, F06b, F18 e F19 aprovados): listagem com os três eixos
 * (documento, pagamento, conciliação), ficha da obrigação, Nova despesa (avulsa, parcelada, "Já foi paga"),
 * registrar pagamento (parcial, várias obrigações, excedente discriminado), estorno por inclusão, completar documento
 * depois do pagamento e períodos fechados. A4: abas Cartões e Reembolsos e "Quem pagou" (conta, cartão ou pessoa) em
 * CartoesReembolsos.tsx; conciliação é etapa própria.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api, consulta, ErroApi } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { navegar } from '../rotas.ts';
import { useRotuloTrilha } from '../componentes/trilha.tsx';
import { Campo } from '../componentes/basicos.tsx';
import { Abas, Ajuda, CampoBusca, CardLista, CardsLista, Codigo, ComCodigos, Estado, EstadoCarga, Modal, RolagemIndicada, SeloSituacao, TextoPartes, Th, useCarga, useOrdenacao, type Tom } from '../comercial/ui.tsx';
import { BarraFiltros } from '../comercial/filtros.tsx';
import { CampoData, hojeIso } from '../comercial/CampoData.tsx';
import { moeda2 } from '../comercial/proposta/formatos.ts';
import { arquivoParaApi } from '../comercial/Pedidos.tsx';
import { ReprogramarVencimento } from './Reprogramar.tsx';
import { AbaCartoes, AbaReembolsos, CamposCartao, CamposPessoa, EscolhaQuemPagou, origemInicial, payloadOrigem, pendenciaOrigem, type Origem } from './CartoesReembolsos.tsx';

/* ---------- tipos da API ---------- */
type SituacaoPagamento = 'aberto' | 'parcial' | 'pago' | 'cancelada';
interface Obrigacao {
  id: string;
  numero: string;
  despesaId: string;
  despesaNumero: string;
  tipo: 'avulsa' | 'parcelada' | 'recorrente';
  parcela: number;
  parcelas: number;
  descricao: string;
  vencimento: string;
  competencia: string;
  valor: string;
  pago: string;
  desconto: string;
  creditoDocumento: string;
  saldo: string;
  situacaoPagamento: SituacaoPagamento;
  vencida: boolean;
  diasAtraso: number;
  venceHoje: boolean;
  conciliacao: 'nao_conciliado' | 'conciliado' | null;
  documento: {
    estado: 'estimado' | 'confirmado';
    tipo: string | null;
    numero: string | null;
    emissao: string | null;
  };
  favorecido: { nome: string; tipo: string; documento: string | null };
  conta: { codigo: string; nome: string };
  centro: { codigo: string; nome: string } | null;
  cancelada: boolean;
  recorrencia: { id: string; numero: string; seq: number; frequencia: string } | null;
  projetado: boolean;
  valorAntesDocumento?: string | null;
  zeradaPeloDocumento?: boolean;
}
interface Resumo {
  valor: string;
  quantidade: number;
}
interface Listagem {
  base: 'vencimento' | 'competencia';
  periodo: { de: string | null; ate: string | null };
  referencia: string;
  resumo: {
    vencidas: Resumo;
    vencemHoje: Resumo;
    aVencer: Resumo;
    pagoNoPeriodo: Resumo;
    saldoEmAberto: string;
    projetado: { vencidas: Resumo; vencemHoje: Resumo; aVencer: Resumo; total: Resumo };
  };
  itens: Obrigacao[];
}
interface Alocacao {
  obrigacao: string;
  tipo: 'principal' | 'juros' | 'multa' | 'credito_recuperar';
  valor: string;
  justificativa: string | null;
  conta: string | null;
}
interface Pagamento {
  id: string;
  numero: string;
  data: string;
  valor: string;
  origem: string;
  conta: string | null;
  meio: string;
  identificador: string | null;
  registradoPor: string | null;
  estorno: { em: string; por: string; justificativa: string } | null;
  conciliacao?: 'nao_conciliado' | 'conciliado' | null;
  conciliacaoNumero?: string | null;
  alocacoes: Alocacao[];
  /** A4: cobrança no cartão (fatura atual) ou pagamento feito por pessoa (escolha do reembolso) */
  cartao?: { apelido: string; final: string; fatura: { numero: string; mes: string; situacao: 'sugerida' | 'conferida' } | null } | null;
  pessoa?: { nome: string; escolha: 'gera' | 'reembolso' | 'sem'; reembolso: string | null; justificativa: string | null } | null;
}
interface Anexo {
  id: string;
  papel: 'documento' | 'comprovante';
  pagamento_id: string | null;
  nome_original: string;
  retirado_em: string | null;
  retirado_por: string | null;
  motivo_retirada: string | null;
  enviado_por: string | null;
  criado_em: string;
}
interface Ficha {
  obrigacao: Obrigacao;
  despesa: {
    id: string;
    numero: string;
    tipo: 'avulsa' | 'parcelada' | 'recorrente';
    descricao: string;
    competencia: string;
    valorEstimado: string;
    valorDocumento: string | null;
    estadoDocumento: 'estimado' | 'confirmado';
    cancelada: { em: string; motivo: string } | null;
    planoContasId: string;
    centroCustoId: string | null;
    atualizadoEm: string;
    periodoFechado: boolean;
  };
  parcelas: Obrigacao[];
  pagamentos: Pagamento[];
  /** E3 (0056): vencimento original e histórico de reprogramação */
  vencimento: { original: string | null; atual: string; reprogramacoes: { anterior: string; novo: string; motivo: string | null; em: string; por: string | null; fonte: 'registro' | 'auditoria' }[] } | null;
  podeReprogramar: boolean;
  /** crédito a recuperar por obrigação (0051): documento + pagamento a maior; recuperado na fatura e em dinheiro (RCP); saldo único */
  creditosARecuperar: {
    obrigacao: string;
    valor: string;
    justificativa: string | null;
    origem: 'documento' | 'pagamento_a_maior' | 'misto';
    origens: { tipo: 'documento' | 'pagamento_a_maior'; valor: string; pagamento: string | null; justificativa: string | null }[];
    pagamentosOrigem: string[];
    gerado: string;
    recuperado: string;
    saldo: string;
    recuperacoes: { tipo: 'fatura' | 'dinheiro'; numero: string; valor: string; data: string | null; em: string; fatura: string | null; mes: string | null; conta: string | null; conciliacao: string | null; movimentos: string | null; estorno: { justificativa: string; em: string; por: string | null } | null }[];
    podeRegistrarRecuperacao: boolean;
    podeEstornarRecuperacao: boolean;
  }[];
  ajustes: {
    id: string;
    tipo: 'desconto' | 'credito_documento' | 'desconto_reversao' | 'credito_reversao';
    valor: string;
    justificativa: string;
    em: string;
    obrigacao: string;
    pagamento: string | null;
    origem?: 'estorno' | 'documento' | null;
    conta: string;
    autor: string | null;
    estornado: boolean;
  }[];
  anexos: Anexo[];
  recorrencia: Recorrencia | null;
  redistribuicoes: {
    id: string;
    em: string;
    autor: string | null;
    valorAnterior: string;
    valorDocumento: string;
    credito: string;
    justificativa: string;
    justificativaCredito: string | null;
    parcelas: { numero: string; parcela: number; anterior: string; novo: string; pago: string; credito: string; cancelada: boolean }[];
  }[];
}
interface Recorrencia {
  id: string;
  numero: string;
  frequencia: string;
  frequenciaMeses: number;
  valorPrevisto: string;
  diaVencimento: number;
  primeiraCompetencia: string;
  primeiroVencimento: string;
  termino: { ocorrencias: number } | null;
  horizonteMeses: number | null;
  encerrada: { aPartir: string; em: string; motivo: string } | null;
  estaOcorrencia: number;
  geradas: number;
  projetadaAte: string | null;
  proxima: { competencia: string; vencimento: string } | null;
  planoContasId: string;
  centroCustoId: string | null;
  descricao: string;
  eventos: { acao: string; a_partir: string; campos: Record<string, unknown>; afetadas: string[]; preservadas: { numero: string; motivo: string }[]; motivo: string; data_hora: string; autor: string | null }[];
  ocorrencias: (Obrigacao & { seq: number })[];
}
export interface Favorecido {
  tipo: 'parceiro' | 'pessoa';
  id: string;
  nome: string;
  documento: string;
  detalhe: string;
}
interface ContaGerencial {
  id: string;
  codigo: string;
  nome: string;
  status: string;
  pode_gerar_obrigacao?: boolean;
  somente_guia_recebida?: boolean;
}
interface CentroCusto {
  id: string;
  codigo: string;
  nome: string;
  status: string;
}
export interface ContaBancaria {
  id: string;
  banco: string;
  agencia: string;
  numero: string;
  principal?: boolean;
  status: string;
}
interface Periodo {
  mes: string;
  fechado: boolean;
  situacao?: 'aberto' | 'fechado' | 'fechado_com_pendencias';
  pendencias?: number;
  ultimoEvento: {
    acao: string;
    justificativa: string;
    em: string;
    por: string | null;
  };
}

/* ---------- formatos ---------- */
export const rs = (v: string | number | null | undefined) => moeda2(Number(v ?? 0));
export const dataBr = (iso: string | null | undefined) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');
const mesBr = (aaaaMm: string) => aaaaMm.split('-').reverse().join('/');
const NOME_MES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
export const mesExtenso = (aaaaMm: string) => `${NOME_MES[Number(aaaaMm.slice(5, 7)) - 1]}/${aaaaMm.slice(0, 4)}`;
/** "1.234,56", "R$ 59,00" ou "59.9" → centavos (NaN se inválido). */
export function centavosDe(t: string): number {
  const s = t.replace(/R\$|\s/g, '');
  if (!s) return NaN;
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}
export const deCentavos = (c: number) =>
  (c / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const somarMeses = (iso: string, meses: number) => {
  const [a, m, d] = iso.split('-').map(Number);
  const alvo = new Date(Date.UTC(a, m - 1 + meses, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(d, ultimo));
  return alvo.toISOString().slice(0, 10);
};
/** Vencimento no mês de `primeiro` + `meses`, no dia escolhido (ou o último dia válido do mês). */
const vencimentoNoMes = (primeiro: string, meses: number, dia: number) => {
  const [a, m] = primeiro.split('-').map(Number);
  const alvo = new Date(Date.UTC(a, m - 1 + meses, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(dia, ultimo));
  return alvo.toISOString().slice(0, 10);
};
const ordinal = (n: number) => `${n}ª`;
const FREQUENCIAS: Record<string, string> = { '1': 'Mensal', '2': 'Bimestral', '3': 'Trimestral', '6': 'Semestral', '12': 'Anual' };
const dividirParcelas = (total: number, n: number) => {
  const base = Math.floor(total / n);
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? total - base * (n - 1) : base));
};
export const TIPOS_DOCUMENTO: Record<string, string> = {
  fatura: 'Fatura',
  nota_fiscal: 'Nota fiscal',
  boleto: 'Boleto',
  recibo: 'Recibo',
  guia: 'Guia',
  contrato: 'Contrato',
  outro: 'Outro',
};
export const MEIOS: Record<string, string> = {
  pix: 'PIX',
  boleto: 'Boleto',
  transferencia: 'Transferência',
  debito_automatico: 'Débito automático',
  dinheiro: 'Dinheiro',
  outro: 'Outro',
  cartao: 'Cartão da Inttrix',
};
const SITUACAO: Record<SituacaoPagamento, { texto: string; tom: Tom }> = {
  aberto: { texto: 'Em aberto', tom: 'aberto' },
  parcial: { texto: 'Parcial', tom: 'parcial' },
  pago: { texto: 'Pago', tom: 'positivo' },
  cancelada: { texto: 'Cancelada', tom: 'encerrado' },
};
const ROTULO_ALOCACAO: Record<Alocacao['tipo'], string> = {
  principal: 'Principal',
  juros: 'Juros',
  multa: 'Multa',
  credito_recuperar: 'Crédito a recuperar',
};
const rotuloAjuste = (x: Ficha['ajustes'][number]) =>
  x.tipo === 'desconto'
    ? 'Desconto para quitação'
    : x.tipo === 'credito_documento'
      ? 'Crédito a recuperar (documento)'
      : x.tipo === 'desconto_reversao'
        ? 'Reversão de desconto por alteração do documento'
        : x.origem === 'estorno'
          ? 'Reversão de crédito por estorno'
          : 'Reversão de crédito por alteração do documento';
/** Abre a aba Cartões já com a ficha da fatura (link a partir da ficha da despesa). */
const abrirFatura = (numero: string) => {
  try {
    sessionStorage.setItem('inttrix:contas-pagar:aba', 'cartoes');
    sessionStorage.setItem('inttrix:contas-pagar:abrir-fatura', numero);
  } catch {
    /* sem armazenamento da sessão: abre só a aba Obrigações */
  }
  navegar('/contas-pagar');
};
export const novaChave = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

function SeloPagamento({ o }: { o: Obrigacao }) {
  // valor efetivo zero pelo documento, com pagamento preservado (todo o pago virou crédito a recuperar)
  if (o.zeradaPeloDocumento) return <SeloSituacao texto="Zerada pelo documento" tom="encerrado" />;
  const s = SITUACAO[o.situacaoPagamento];
  return (
    <span className="celula-situacao-pagar">
      <SeloSituacao texto={s.texto} tom={s.tom} />
      {o.vencida && (
        <small className="texto-alerta" data-teste={`vencida-${o.numero}`}>
          vencida há {plural(o.diasAtraso, 'dia', 'dias')}
        </small>
      )}
      {o.venceHoje && <small className="texto-alerta">vence hoje</small>}
    </span>
  );
}
const SeloDocumento = ({ estado }: { estado: 'estimado' | 'confirmado' }) => <span className={`selo-documento-fin ${estado}`}>{estado === 'confirmado' ? 'Confirmado' : 'Estimado'}</span>;
export const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
const textoConciliacao = (c: string | null) => (c === 'conciliado' ? 'Conciliado' : c ? 'Não conciliado' : '—');

/* ---------- carga de apoio (classificação e contas da Inttrix) ---------- */
export function useApoio() {
  return useCarga(async () => {
    const [contas, centros, bancos] = await Promise.all([
      api.get<ContaGerencial[]>('/api/plano-contas'),
      api.get<CentroCusto[]>('/api/centros-custo'),
      api.get<ContaBancaria[]>('/api/contas-bancarias'),
    ]);
    return {
      contasObrigacao: contas.filter((c) => c.status === 'ativo' && c.pode_gerar_obrigacao),
      // contas de guias (DAS e demais): só no lançamento da guia recebida
      contasDespesa: contas.filter((c) => c.status === 'ativo' && c.pode_gerar_obrigacao && !c.somente_guia_recebida),
      contasAtivas: contas.filter((c) => c.status === 'ativo'),
      centros: centros.filter((c) => c.status === 'ativo'),
      bancos: bancos.filter((b) => b.status === 'ativo').sort((a, b) => Number(Boolean(b.principal)) - Number(Boolean(a.principal))),
    };
  }, []);
}
type Apoio = NonNullable<ReturnType<typeof useApoio>['dados']>;
export const nomeBanco = (b: ContaBancaria) => `${b.banco} · ag. ${b.agencia} · ${b.numero}${b.principal ? ' (principal)' : ''}`;

/* ====================================================================== listagem (F01) */
type PeriodoLista = 'mes' | 'anterior' | 'proximo' | 'ano' | 'tudo';
const PERIODOS: { id: PeriodoLista; nome: string }[] = [
  { id: 'mes', nome: 'Este mês' },
  { id: 'anterior', nome: 'Mês anterior' },
  { id: 'proximo', nome: 'Próximo mês' },
  { id: 'ano', nome: 'Este ano' },
  { id: 'tudo', nome: 'Todo o período' },
];
function intervalo(p: PeriodoLista, hoje: string): { de?: string; ate?: string } {
  const inicio = `${hoje.slice(0, 7)}-01`;
  const ultimoDia = (ini: string) => new Date(Date.UTC(Number(ini.slice(0, 4)), Number(ini.slice(5, 7)), 0)).toISOString().slice(0, 10);
  if (p === 'mes') return { de: inicio, ate: ultimoDia(inicio) };
  if (p === 'anterior')
    return {
      de: somarMeses(inicio, -1),
      ate: ultimoDia(somarMeses(inicio, -1)),
    };
  if (p === 'proximo') return { de: somarMeses(inicio, 1), ate: ultimoDia(somarMeses(inicio, 1)) };
  if (p === 'ano')
    return {
      de: `${hoje.slice(0, 4)}-01-01`,
      ate: `${hoje.slice(0, 4)}-12-31`,
    };
  return {};
}
type FiltroPagamento = '' | 'aberto' | 'vencidas' | 'parcial' | 'pago' | 'cancelada';
type ColPagar = 'vencimento' | 'favorecido' | 'obrigacao' | 'documento' | 'valor' | 'pago' | 'saldo' | 'pagamento';

/* filtros da listagem: padrão "Todo o período"; os escolhidos ficam na sessão do navegador (ir à ficha e voltar não os perde) */
interface FiltrosPagar {
  base: 'vencimento' | 'competencia';
  periodo: PeriodoLista;
  situacao: FiltroPagamento;
  mostrarProjecoes: boolean;
  busca: string;
}
const FILTROS_PADRAO: FiltrosPagar = { base: 'vencimento', periodo: 'tudo', situacao: '', mostrarProjecoes: true, busca: '' };
const CHAVE_FILTROS = 'inttrix:contas-pagar:filtros';
function lerFiltros(): FiltrosPagar {
  try {
    const x = JSON.parse(sessionStorage.getItem(CHAVE_FILTROS) ?? 'null') as Partial<FiltrosPagar> | null;
    return x ? { ...FILTROS_PADRAO, ...x } : FILTROS_PADRAO;
  } catch {
    return FILTROS_PADRAO;
  }
}

type AbaPagar = 'obrigacoes' | 'cartoes' | 'reembolsos';
const CHAVE_ABA = 'inttrix:contas-pagar:aba';
export function ListaContasPagar() {
  const [aba, setAba] = useState<AbaPagar>(() => {
    try {
      const x = sessionStorage.getItem(CHAVE_ABA);
      return x === 'cartoes' || x === 'reembolsos' ? x : 'obrigacoes';
    } catch {
      return 'obrigacoes';
    }
  });
  const trocar = (a: AbaPagar) => {
    setAba(a);
    try {
      sessionStorage.setItem(CHAVE_ABA, a);
    } catch {
      /* sem armazenamento da sessão: a aba só não sobrevive à navegação */
    }
  };
  const abas = (
    <Abas
      rotulo="Contas a Pagar"
      atual={aba}
      aoTrocar={trocar}
      abas={[
        { id: 'obrigacoes', rotulo: 'Obrigações' },
        { id: 'cartoes', rotulo: 'Cartões' },
        { id: 'reembolsos', rotulo: 'Reembolsos' },
      ]}
    />
  );
  if (aba === 'cartoes') return <AbaCartoes abas={abas} />;
  if (aba === 'reembolsos') return <AbaReembolsos abas={abas} />;
  return <ListaObrigacoes abas={abas} />;
}

function ListaObrigacoes({ abas }: { abas: ReactNode }) {
  const { pode } = useSessao();
  const hoje = hojeIso();
  const inicial = useMemo(lerFiltros, []);
  const [base, setBase] = useState<'vencimento' | 'competencia'>(inicial.base);
  const [periodo, setPeriodo] = useState<PeriodoLista>(inicial.periodo);
  const [situacao, setSituacao] = useState<FiltroPagamento>(inicial.situacao);
  const [mostrarProjecoes, setMostrarProjecoes] = useState(inicial.mostrarProjecoes);
  const [guia, setGuia] = useState(false);
  const [busca, setBusca] = useState(inicial.busca);
  const [nova, setNova] = useState(false);
  useEffect(() => {
    try {
      sessionStorage.setItem(CHAVE_FILTROS, JSON.stringify({ base, periodo, situacao, mostrarProjecoes, busca } satisfies FiltrosPagar));
    } catch {
      /* sem armazenamento da sessão: os filtros só não sobrevivem à navegação */
    }
  }, [base, periodo, situacao, mostrarProjecoes, busca]);
  const [pagar, setPagar] = useState<Obrigacao[] | null>(null);
  const [selecionadas, setSelecionadas] = useState<string[]>([]);
  const [periodos, setPeriodos] = useState(false);
  const faixa = intervalo(periodo, hoje);
  const carga = useCarga(() => api.get<Listagem>(`/api/contas-pagar${consulta({ base, de: faixa.de, ate: faixa.ate })}`), [base, periodo]);
  const ord = useOrdenacao<ColPagar>('vencimento');
  const podePagar = pode('registrar', 'pagamento');
  const dados = carga.dados;
  const termo = busca.trim().toLowerCase();
  const filtrados = (dados?.itens ?? []).filter(
    (o) =>
      (!termo || [o.numero, o.despesaNumero, o.descricao, o.favorecido.nome, o.favorecido.documento ?? '', o.documento.numero ?? ''].some((x) => x.toLowerCase().includes(termo))) &&
      (!situacao || (situacao === 'vencidas' ? o.vencida : o.situacaoPagamento === situacao)) &&
      (mostrarProjecoes || !o.projetado),
  );
  const lista = ord.ordenar(
    filtrados,
    {
      vencimento: (o) => o.vencimento,
      favorecido: (o) => o.favorecido.nome,
      obrigacao: (o) => o.descricao,
      documento: (o) => o.documento.numero ?? '',
      valor: (o) => Number(o.valor),
      pago: (o) => Number(o.pago),
      saldo: (o) => Number(o.saldo),
      pagamento: (o) => ['aberto', 'parcial', 'pago', 'cancelada'].indexOf(o.situacaoPagamento),
    },
    (a, b) => a.numero.localeCompare(b.numero),
  );
  const abertasSelecionadas = lista.filter((o) => selecionadas.includes(o.id) && Number(o.saldo) > 0);
  const limpar = () => (setBusca(FILTROS_PADRAO.busca), setSituacao(FILTROS_PADRAO.situacao), setPeriodo(FILTROS_PADRAO.periodo), setBase(FILTROS_PADRAO.base), setMostrarProjecoes(FILTROS_PADRAO.mostrarProjecoes));
  const r = dados?.resumo;
  // opção A (aprovada): o LANÇADO é o valor principal; o projetado vem numa linha separada, nunca somado
  const comProjetado = (texto: string, p?: Resumo) => (
    <>
      {texto}
      {p && p.quantidade > 0 && (
        <span className="detalhe-projetado" data-teste="linha-projetado">
          + {rs(p.valor)} projetado ({p.quantidade})
        </span>
      )}
    </>
  );
  const periodoTexto = periodo === 'tudo' ? 'todo o período' : `${dataBr(faixa.de)} a ${dataBr(faixa.ate)}`;
  return (
    <>
      <h1 className="titulo-pagina rotulo-oculto">Contas a Pagar</h1>
      {r && (
        <CardsLista n={4} teste="cards-pagar">
          <CardLista
            icone="alerta"
            tom="vermelho"
            rotulo="Vencidas"
            valor={rs(r.vencidas.valor)}
            detalhe={comProjetado(`${plural(r.vencidas.quantidade, 'obrigação', 'obrigações')} · saldo em aberto`, r.projetado?.vencidas)}
            ajuda={<Ajuda titulo="Vencidas">Obrigações lançadas com saldo e vencimento anterior a hoje, dentro do período e da data considerada. Ocorrências projetadas aparecem só na linha “projetado”, sem somar.</Ajuda>}
            teste="card-vencidas"
          />
          <CardLista
            icone="relogio"
            tom="roxo"
            rotulo="Vencem hoje"
            valor={rs(r.vencemHoje.valor)}
            detalhe={comProjetado(`${plural(r.vencemHoje.quantidade, 'obrigação', 'obrigações')} · ${dataBr(dados.referencia)}`, r.projetado?.vencemHoje)}
            teste="card-vencem-hoje"
          />
          <CardLista
            icone="documento"
            tom="azul"
            rotulo="A vencer no período"
            valor={rs(r.aVencer.valor)}
            detalhe={comProjetado(`${plural(r.aVencer.quantidade, 'obrigação', 'obrigações')} · saldo`, r.projetado?.aVencer)}
            ajuda={<Ajuda titulo="A vencer no período">Saldo das obrigações lançadas que vencem depois de hoje, no período. O valor projetado das recorrências (estimado, sem documento nem pagamento) aparece na linha de baixo e não entra no total.</Ajuda>}
            teste="card-a-vencer"
          />
          <CardLista
            icone="dinheiro"
            tom="verde"
            rotulo="Pago no período"
            valor={rs(r.pagoNoPeriodo.valor)}
            detalhe={`${plural(r.pagoNoPeriodo.quantidade, 'pagamento', 'pagamentos')} · data do pagamento`}
            ajuda={<Ajuda titulo="Pago no período">Saídas da conta da Inttrix (pagamentos ativos; estornados não entram) pela DATA DO PAGAMENTO, no período escolhido. Cobrança no cartão e pagamento feito por pessoa não são saída: a saída é o pagamento da fatura ou do reembolso.</Ajuda>}
            teste="card-pago-periodo"
          />
        </CardsLista>
      )}
      <p className="linha-base-pagar texto-secundario" data-teste="base-pagar">
        <span>
          Base: <strong>{base === 'vencimento' ? 'vencimento' : 'competência'}</strong> · {periodoTexto} · referência {dataBr(dados?.referencia ?? hoje)}
        </span>
        <Ajuda titulo="Como ler esta lista">
          Cada obrigação mostra três situações independentes: documento (estimado ou confirmado), pagamento (em aberto, parcial, pago) e conciliação com o extrato (“Conciliado” quando todos os pagamentos pela conta estão vinculados a movimentos do extrato; feita na tela Conciliação bancária). Ocorrências de recorrência ainda sem documento nem pagamento aparecem como “Projetado”. Compras no cartão e despesas pagas por pessoas aparecem aqui uma vez; a dívida com o emissor fica na aba Cartões e a com a pessoa, na aba Reembolsos — nunca somadas duas vezes.
        </Ajuda>
      </p>
      <div className="linha-abas-acao">
        {abas}
        <div className="acoes-direita">
          {(pode('fechar', 'periodo_financeiro') || pode('reabrir', 'periodo_financeiro')) && (
            <button type="button" className="botao-secundario" onClick={() => setPeriodos(true)} data-teste="abrir-periodos">
              Períodos financeiros
            </button>
          )}
          {podePagar && abertasSelecionadas.length > 0 && (
            <button type="button" className="botao-secundario" onClick={() => setPagar(abertasSelecionadas)} data-teste="pagar-selecionadas">
              Registrar pagamento ({abertasSelecionadas.length})
            </button>
          )}
          {pode('lancar', 'conta_pagar') && (
            <button type="button" className="botao-secundario" onClick={() => setGuia(true)} data-teste="registrar-guia">
              Registrar guia recebida
            </button>
          )}
          {pode('lancar', 'conta_pagar') && (
            <button type="button" className="botao-primario" onClick={() => setNova(true)} data-teste="nova-despesa">
              Nova despesa
            </button>
          )}
        </div>
      </div>
      <BarraFiltros
        teste="pagar"
        aoLimpar={limpar}
        direita={
          <Ajuda titulo="Como buscar">
            Busca por nº da obrigação (PAG-) ou da despesa (DSP-), descrição, favorecido, CNPJ e nº do documento. A data considerada muda a base do período e dos cards.
          </Ajuda>
        }
        fixos={
          <>
            <CampoBusca
              valor={busca}
              setValor={setBusca}
              rotulo="Buscar obrigação"
              ajuda="Favorecido, descrição, nº do documento"
              dica="PAG- ou DSP-, descrição, favorecido, CNPJ ou nº do documento"
              teste="busca-pagar"
              total={termo ? lista.length : null}
            />
            <label className="filtro-fixo">
              Data considerada
              <select value={base} onChange={(e) => setBase(e.target.value as 'vencimento' | 'competencia')} data-teste="base-data">
                <option value="vencimento">Vencimento</option>
                <option value="competencia">Competência</option>
              </select>
            </label>
            <label className="filtro-fixo">
              Período
              <select value={periodo} onChange={(e) => setPeriodo(e.target.value as PeriodoLista)} data-teste="periodo-pagar">
                {PERIODOS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </label>
            <label className="filtro-fixo">
              Pagamento
              <select value={situacao} onChange={(e) => setSituacao(e.target.value as FiltroPagamento)} data-teste="filtro-pagamento">
                <option value="">Todos</option>
                <option value="aberto">Em aberto</option>
                <option value="vencidas">Vencidas</option>
                <option value="parcial">Parcial</option>
                <option value="pago">Pago</option>
                <option value="cancelada">Cancelada</option>
              </select>
            </label>
            <label className="caixa-marcar filtro-projecoes">
              <input type="checkbox" checked={mostrarProjecoes} onChange={(e) => setMostrarProjecoes(e.target.checked)} data-teste="mostrar-projecoes" />
              <span>Mostrar projeções</span>
            </label>
          </>
        }
      />
      {!dados ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="pagar" />
      ) : lista.length === 0 ? (
        <p className="vazio" data-teste="pagar-vazia">
          Nenhuma obrigação {termo ? 'com a busca e ' : 'com '}os filtros atuais.
          {pode('lancar', 'conta_pagar') ? ' Use “Nova despesa” para lançar.' : ''}
        </p>
      ) : (
        <RolagemIndicada className="rolagem quadro-tabela" teste="pagar">
          <table className="tabela tabela-pagar" data-teste="lista-pagar">
            <thead>
              <tr>
                {podePagar && (
                  <th className="col-selecao">
                    <span className="rotulo-oculto">Selecionar</span>
                  </th>
                )}
                <Th ord={ord} coluna="vencimento">
                  {base === 'vencimento' ? 'Vencimento' : 'Vencimento · competência'}
                </Th>
                <Th ord={ord} coluna="favorecido">
                  Favorecido
                </Th>
                <Th ord={ord} coluna="obrigacao">
                  Obrigação
                </Th>
                <Th ord={ord} coluna="documento">
                  Documento
                </Th>
                <Th ord={ord} coluna="valor" numero>
                  Valor
                </Th>
                <Th ord={ord} coluna="pago" numero>
                  Pago
                </Th>
                <Th ord={ord} coluna="saldo" numero>
                  Saldo
                </Th>
                <Th ord={ord} coluna="pagamento">
                  Pagamento
                </Th>
                <Th>Conciliação</Th>
              </tr>
            </thead>
            <tbody>
              {lista.map((o) => (
                <tr key={o.id} className={o.projetado ? 'linha-projetada' : undefined} data-teste={`linha-${o.numero}`}>
                  {podePagar && (
                    <td className="col-selecao">
                      {Number(o.saldo) > 0 && (
                        <input
                          type="checkbox"
                          aria-label={`Selecionar ${o.numero} para pagamento`}
                          checked={selecionadas.includes(o.id)}
                          onChange={(e) => setSelecionadas(e.target.checked ? [...selecionadas, o.id] : selecionadas.filter((x) => x !== o.id))}
                          data-teste={`selecionar-${o.numero}`}
                        />
                      )}
                    </td>
                  )}
                  <td className="sem-quebra">
                    {dataBr(o.vencimento)}
                    {base === 'competencia' && <small className="texto-secundario bloco">comp. {mesBr(o.competencia)}</small>}
                  </td>
                  <td>
                    {o.favorecido.nome}
                    {o.favorecido.documento && <small className="texto-secundario bloco sem-quebra">{o.favorecido.documento}</small>}
                  </td>
                  <td>
                    <a href={`#/contas-pagar/${o.numero}`} className="link-forte" data-teste={`abrir-${o.numero}`}>
                      {o.descricao}
                    </a>
                    <small className="texto-secundario bloco sem-quebra">
                      {o.numero} · {o.tipo === 'parcelada' ? `parcela ${o.parcela}/${o.parcelas}` : o.recorrencia ? `recorrente ${o.recorrencia.frequencia} · ${o.recorrencia.numero}` : 'avulsa'}
                    </small>
                  </td>
                  <td>
                    {o.documento.numero ? (
                      <span className="sem-quebra">
                        {o.documento.tipo ? `${TIPOS_DOCUMENTO[o.documento.tipo]} ` : ''}
                        {o.documento.numero}
                      </span>
                    ) : (
                      <span className="texto-secundario">{o.projetado ? 'sem documento' : 'sem nº'}</span>
                    )}
                    <span className="bloco">{o.projetado ? <span className="selo-projetado">Projetado</span> : <SeloDocumento estado={o.documento.estado} />}</span>
                  </td>
                  <td className="numero">{rs(o.valor)}</td>
                  <td className="numero">{rs(o.pago)}</td>
                  <td className="numero">{rs(o.saldo)}</td>
                  <td>
                    <SeloPagamento o={o} />
                  </td>
                  <td className="texto-secundario sem-quebra">{textoConciliacao(o.conciliacao)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RolagemIndicada>
      )}
      {guia && <GuiaRecebida aoFechar={() => setGuia(false)} aoSalvar={(numero) => (setGuia(false), navegar(`/contas-pagar/${numero}`))} />}
      {nova && <NovaDespesa aoFechar={() => setNova(false)} aoSalvar={(numero) => (setNova(false), numero ? navegar(`/contas-pagar/${numero}`) : void carga.recarregar())} />}
      {pagar && <RegistrarPagamento obrigacoes={pagar} aoFechar={() => setPagar(null)} aoSalvar={() => (setPagar(null), setSelecionadas([]), void carga.recarregar())} />}
      {periodos && <PeriodosFinanceiros aoFechar={() => (setPeriodos(false), void carga.recarregar())} />}
    </>
  );
}

/* ====================================================================== favorecido (busca unificada) */
export function BuscaFavorecido({ valor, aoEscolher }: { valor: Favorecido | null; aoEscolher: (f: Favorecido | null) => void }) {
  const { pode } = useSessao();
  const [texto, setTexto] = useState('');
  const [achados, setAchados] = useState<Favorecido[]>([]);
  const [buscou, setBuscou] = useState('');
  const [cadastrar, setCadastrar] = useState(false);
  const [cadastrado, setCadastrado] = useState<string | null>(null);
  // M01/M08: "Cadastrar favorecido" só aparece para quem pode criar Parceiro ou Pessoa financeira (nada é concedido aqui)
  const podeCadastrar = pode('criar', 'parceiro') || pode('criar', 'pessoa_financeira');
  useEffect(() => {
    if (texto.trim().length < 2) return (setAchados([]), setBuscou(''));
    const t = setTimeout(
      () =>
        void api
          .get<Favorecido[]>(`/api/contas-pagar/favorecidos${consulta({ busca: texto.trim() })}`)
          .then((r) => (setAchados(r), setBuscou(texto.trim())))
          .catch(() => setAchados([])),
      250,
    );
    return () => clearTimeout(t);
  }, [texto]);
  // fora do formulário da despesa (portal): Enter num campo do cadastro nunca envia a despesa
  const modal =
    cadastrar &&
    createPortal(
      <CadastrarFavorecido
        termo={texto.trim()}
        aoCancelar={() => setCadastrar(false)}
        aoConcluir={(f, aviso) => {
          setCadastrar(false);
          setCadastrado(aviso);
          aoEscolher(f);
        }}
      />,
      document.body,
    );
  if (valor)
    return (
      <>
        <div className="favorecido-escolhido" data-teste="favorecido-escolhido">
          <span>
            <strong>{valor.nome}</strong>{' '}
            <small className="texto-secundario">
              {valor.documento}
              {valor.detalhe && valor.detalhe !== valor.nome ? ` · ${valor.detalhe}` : ''}
            </small>
          </span>
          <button type="button" className="botao-link" onClick={() => (setCadastrado(null), aoEscolher(null))} data-teste="trocar-favorecido">
            Trocar
          </button>
        </div>
        {modal}
        {cadastrado && (
          <p className="aviso aviso-sucesso" role="status" data-teste="favorecido-cadastrado">
            {cadastrado}
          </p>
        )}
      </>
    );
  const termo = texto.trim();
  const semResultado = termo.length >= 2 && buscou === termo && achados.length === 0;
  return (
    <div className="busca-favorecido">
      <input type="search" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Nome, razão social ou CNPJ" aria-label="Buscar favorecido" data-teste="busca-favorecido" />
      {(achados.length > 0 || (semResultado && podeCadastrar)) && (
        <ul className="opcoes-favorecido" role="listbox" aria-label="Favorecidos encontrados">
          {achados.map((f) => (
            <li key={`${f.tipo}-${f.id}`}>
              <button type="button" role="option" aria-selected={false} onClick={() => aoEscolher(f)} data-teste={`favorecido-${f.nome}`}>
                <strong>{f.nome}</strong>
                <small>
                  {f.documento} · {f.tipo === 'parceiro' ? (f.detalhe !== f.nome ? `Parceiro ${f.detalhe}` : 'Parceiro comercial') : f.detalhe}
                </small>
              </button>
            </li>
          ))}
          {semResultado && <li className="sem-resultado">Nenhum favorecido ativo com “{termo}” em Parceiros comerciais ou Pessoas financeiras.</li>}
          {podeCadastrar && termo.length >= 2 && (
            <li>
              <button type="button" className="cadastrar-favorecido" onClick={() => setCadastrar(true)} data-teste="cadastrar-favorecido">
                + Cadastrar favorecido “{termo}”
              </button>
            </li>
          )}
        </ul>
      )}
      {semResultado && !podeCadastrar && <small className="texto-secundario">Nenhum favorecido ativo encontrado (Parceiros comerciais e Pessoas financeiras).</small>}
      {modal}
    </div>
  );
}

/* ---------- cadastro de favorecido dentro da despesa (M02–M08): a despesa por baixo fica intacta ---------- */
type TipoFavorecido = 'empresa' | 'pf' | 'estrangeiro' | 'orgao_sem_cnpj';
interface SituacaoCnpj {
  cnpj: string;
  jaCadastrado: { favorecido: Favorecido; ativo: boolean } | null;
  emCliente: { nome: string } | null;
  mesmaRaiz: {
    id: string;
    nome: string;
    ativo: boolean;
    cnpjReferencia: string;
  }[];
  receita: {
    encontrado: boolean;
    motivo?: string;
    mensagem?: string;
    alerta?: string | null;
    dados?: {
      razaoSocial: string;
      nomeFantasia: string | null;
      situacaoCadastral: string | null;
      matriz: boolean | null;
      endereco: { cidade: string | null; uf: string | null };
    };
  } | null;
}
const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const soDigitos = (t: string) => t.replace(/\D/g, '');
const formatarCnpj = (d: string) => d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
const ROTULO_TIPO_FAV: Record<TipoFavorecido, string> = {
  empresa: 'Empresa com CNPJ',
  pf: 'Pessoa física',
  estrangeiro: 'Estrangeiro',
  orgao_sem_cnpj: 'Órgão sem CNPJ',
};

function CadastrarFavorecido({ termo, aoCancelar, aoConcluir }: { termo: string; aoCancelar: () => void; aoConcluir: (f: Favorecido, aviso: string) => void }) {
  const { pode } = useSessao();
  const podeEmpresa = pode('criar', 'parceiro');
  const podePessoa = pode('criar', 'pessoa_financeira');
  const termoCnpj = soDigitos(termo).length === 14 ? soDigitos(termo) : '';
  const [passo, setPasso] = useState<1 | 2>(1);
  const [tipo, setTipo] = useState<TipoFavorecido>(podeEmpresa ? 'empresa' : 'pf');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  // empresa
  const [cnpj, setCnpj] = useState(termoCnpj ? formatarCnpj(termoCnpj) : '');
  const [sit, setSit] = useState<SituacaoCnpj | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [nomeParceiro, setNomeParceiro] = useState(termoCnpj ? '' : termo);
  const [razao, setRazao] = useState('');
  const [uf, setUf] = useState('');
  const [opcao, setOpcao] = useState<string>('novo'); // 'novo' | id do Parceiro da mesma raiz
  const [justOutra, setJustOutra] = useState('');
  // pessoa
  const [nome, setNome] = useState(termoCnpj ? '' : termo);
  const [cpf, setCpf] = useState('');
  const [pais, setPais] = useState('');
  const [identificacao, setIdentificacao] = useState('');
  const [semDoc, setSemDoc] = useState(false);
  const [justSemDoc, setJustSemDoc] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');

  const digitos = soDigitos(cnpj);
  useEffect(() => {
    if (tipo !== 'empresa' || passo !== 2 || digitos.length !== 14) return setSit(null);
    let vivo = true;
    setConsultando(true);
    setErro(null);
    api
      .get<SituacaoCnpj>(`/api/contas-pagar/favorecidos/cnpj/${digitos}`)
      .then((r) => {
        if (!vivo) return;
        setSit(r);
        const d = r.receita?.encontrado ? r.receita.dados : null;
        if (d) {
          setRazao((x) => x || d.razaoSocial);
          setUf((x) => x || d.endereco.uf || '');
          setNomeParceiro((x) => x || d.nomeFantasia || d.razaoSocial);
        }
        setOpcao(r.mesmaRaiz.find((p) => p.ativo)?.id ?? 'novo');
      })
      .catch((e) => vivo && setErro(e instanceof ErroApi ? e.message : 'Falha ao consultar o CNPJ.'))
      .finally(() => vivo && setConsultando(false));
    return () => {
      vivo = false;
    };
  }, [digitos, tipo, passo]);

  const empresaEmEntidade = tipo === 'empresa' && sit && opcao !== 'novo';
  const alvo = sit?.mesmaRaiz.find((p) => p.id === opcao);
  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      if (tipo === 'empresa') {
        if (!sit) throw new Error('Informe o CNPJ completo (14 dígitos).');
        if (sit.jaCadastrado) {
          if (!sit.jaCadastrado.ativo) throw new Error('A entidade deste CNPJ está inativa em Parceiros comerciais: peça a reativação a quem administra Parceiros.');
          return aoConcluir(sit.jaCadastrado.favorecido, 'Favorecido já cadastrado em Parceiros comerciais e escolhido. Nada novo foi cadastrado; os demais campos foram mantidos.');
        }
        const corpo: Record<string, unknown> = {
          cnpj: digitos,
          razao_social: razao,
          uf,
        };
        if (empresaEmEntidade) Object.assign(corpo, { opcao: 'entidade', parceiro_id: opcao });
        else
          Object.assign(corpo, {
            nome_parceiro: nomeParceiro,
            justificativa: sit.mesmaRaiz.length ? justOutra : undefined,
          });
        const f = await api.post<Favorecido>('/api/contas-pagar/favorecidos/empresa', corpo);
        aoConcluir(
          f,
          empresaEmEntidade
            ? `Entidade acrescentada ao Parceiro ${alvo?.nome ?? ''} (os papéis do Parceiro não mudaram) e escolhida. Os demais campos foram mantidos.`
            : 'Favorecido cadastrado em Parceiros comerciais (papel Fornecedor de despesas) e escolhido. Os demais campos foram mantidos.',
        );
      } else {
        const p = await api.post<{
          id: string;
          nome: string;
          cpf: string | null;
          identificacao_fiscal: string | null;
          pais: string | null;
        }>('/api/pessoas-financeiras', {
          tipo,
          nome,
          cpf: tipo === 'pf' && !semDoc ? cpf : undefined,
          pais: tipo === 'estrangeiro' ? pais : undefined,
          identificacao_fiscal: tipo !== 'pf' && !semDoc ? identificacao || undefined : undefined,
          sem_documento_justificativa: semDoc ? justSemDoc : undefined,
          email: email || undefined,
          telefone: telefone || undefined,
        });
        aoConcluir(
          {
            tipo: 'pessoa',
            id: p.id,
            nome: p.nome,
            documento: p.cpf ? `CPF ${p.cpf}` : (p.identificacao_fiscal ?? 'sem documento'),
            detalhe: ROTULO_TIPO_FAV[tipo],
          },
          'Favorecido cadastrado em Pessoas financeiras e escolhido. Os demais campos foram mantidos.',
        );
      }
    } catch (e) {
      setErro(e instanceof ErroApi || e instanceof Error ? e.message : 'Falha ao cadastrar.');
    } finally {
      setSalvando(false);
    }
  }

  const passos = (
    <p className="passos-cadastro" aria-label="Etapas">
      {passo === 1 ? <b>1. Tipo</b> : <span>1. Tipo</span>} › {passo === 2 ? <b>2. Dados</b> : <span>2. Dados</span>} › <span>3. Conferir</span>
    </p>
  );
  const titulo = passo === 1 ? 'Cadastrar favorecido' : `Cadastrar favorecido — ${ROTULO_TIPO_FAV[tipo]}`;
  const botaoFinal =
    tipo !== 'empresa' ? 'Cadastrar e usar na despesa' : sit?.jaCadastrado ? 'Usar este favorecido' : empresaEmEntidade ? 'Adicionar entidade e usar na despesa' : 'Cadastrar e usar na despesa';
  const bloqueado = tipo === 'empresa' && (!sit || consultando || Boolean(sit.emCliente) || (sit.jaCadastrado ? !sit.jaCadastrado.ativo : false));
  return (
    <Modal
      sobreposto
      medio
      titulo={titulo}
      aoFechar={aoCancelar}
      rodape={
        passo === 1 ? (
          <>
            <button type="button" className="botao-secundario" onClick={aoCancelar} data-teste="voltar-despesa">
              Voltar à despesa
            </button>
            <button type="button" className="botao-primario" onClick={() => setPasso(2)} data-teste="continuar-favorecido">
              Continuar
            </button>
          </>
        ) : (
          <>
            <button type="button" className="botao-secundario" onClick={() => (setPasso(1), setErro(null))} data-teste="voltar-tipo">
              Voltar
            </button>
            <button type="button" className="botao-primario" disabled={salvando || bloqueado} onClick={() => void salvar()} data-teste="salvar-favorecido">
              {salvando ? 'Salvando…' : botaoFinal}
            </button>
          </>
        )
      }
    >
      {passos}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {passo === 1 ? (
        <>
          <fieldset className="opcoes-cartao lista" data-teste="tipo-favorecido">
            <legend>Quem é o favorecido?</legend>
            {(['empresa', 'pf', 'estrangeiro', 'orgao_sem_cnpj'] as TipoFavorecido[]).map((t) => {
              const desab = t === 'empresa' ? !podeEmpresa : !podePessoa;
              const desc =
                t === 'empresa'
                  ? desab
                    ? 'Seu perfil não cadastra Parceiros comerciais. Peça a quem tem a permissão “Criar Parceiro” — nada é concedido automaticamente'
                    : 'Vai para Parceiros comerciais: novo Parceiro com a entidade, ou nova entidade num Parceiro existente'
                  : desab
                    ? 'Seu perfil não cadastra Pessoas financeiras'
                    : t === 'pf'
                      ? 'Vai para Pessoas financeiras (CPF ou “sem documento” com justificativa)'
                      : t === 'estrangeiro'
                        ? 'Pessoas financeiras: país e identificação fiscal'
                        : 'Pessoas financeiras: órgão público sem CNPJ próprio';
              return (
                <label key={t} className={`opcao-cartao${tipo === t ? ' escolhida' : ''}${desab ? ' desabilitada' : ''}`}>
                  <input type="radio" name="tipo-favorecido" checked={tipo === t} disabled={desab} onChange={() => setTipo(t)} data-teste={`tipo-fav-${t}`} />
                  <span>
                    <strong>{ROTULO_TIPO_FAV[t]}</strong>
                    <small>{desc}</small>
                  </span>
                </label>
              );
            })}
          </fieldset>
          <p className="texto-secundario sem-margem">
            O favorecido entra só com o papel <strong>Fornecedor de despesas</strong> (nada de Distribuidor, Locação ou Trade-in). Ao concluir, ele já fica escolhido na despesa e o que você preencheu
            nela continua igual.
          </p>
        </>
      ) : tipo === 'empresa' ? (
        <>
          <div className="grade-form duas">
            <Campo rotulo="CNPJ">
              {(id) => (
                <input
                  id={id}
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                  onBlur={() => digitos.length === 14 && setCnpj(formatarCnpj(digitos))}
                  inputMode="numeric"
                  placeholder="00.000.000/0000-00"
                  data-teste="fav-cnpj"
                />
              )}
            </Campo>
            <div className="campo">
              <span className="rotulo-campo">{sit?.jaCadastrado ? 'Situação' : 'Consulta à Receita'}</span>
              <div className={`alerta-favorecido${sit?.receita?.encontrado || sit?.jaCadastrado ? ' ok' : ''}`} data-teste="fav-receita">
                {digitos.length !== 14
                  ? 'Informe os 14 dígitos do CNPJ.'
                  : consultando
                    ? 'Consultando…'
                    : sit?.jaCadastrado
                      ? '✓ Já cadastrado'
                      : sit?.receita?.encontrado && sit.receita.dados
                        ? `✓ ${sit.receita.dados.razaoSocial}${sit.receita.dados.matriz === false ? ' (filial)' : ''}${sit.receita.dados.situacaoCadastral ? ` · ${sit.receita.dados.situacaoCadastral.toUpperCase()}` : ''}${sit.receita.dados.endereco.cidade ? ` · ${sit.receita.dados.endereco.cidade}/${sit.receita.dados.endereco.uf ?? ''}` : ''}`
                        : (sit?.receita?.mensagem ?? 'Consulta à Receita indisponível agora: preencha os dados manualmente (as validações continuam).')}
              </div>
            </div>
          </div>
          {sit?.receita?.alerta && <p className="alerta-favorecido atencao">{sit.receita.alerta}</p>}
          {sit?.jaCadastrado ? (
            <p className="alerta-favorecido" data-teste="fav-ja-cadastrado">
              <strong>Este CNPJ já está cadastrado</strong> em Parceiros comerciais: <strong>{sit.jaCadastrado.favorecido.detalhe}</strong> (entidade {sit.cnpj},{' '}
              {sit.jaCadastrado.ativo ? 'ativa' : 'inativa'}).
              <br />
              {sit.jaCadastrado.ativo ? 'Nada novo será cadastrado.' : 'A entidade está inativa: peça a reativação a quem administra Parceiros.'}
            </p>
          ) : sit?.emCliente ? (
            <p className="alerta-favorecido bloqueio" data-teste="fav-em-cliente">
              <strong>Este CNPJ está cadastrado como Cliente</strong> ({sit.emCliente.nome}). O Contas a Pagar não altera cadastros comerciais: peça a quem administra Clientes e Parceiros. Nada será
              alterado.
            </p>
          ) : sit ? (
            <>
              {sit.mesmaRaiz.length > 0 ? (
                <>
                  <p className="alerta-favorecido atencao" data-teste="fav-mesma-raiz">
                    <strong>◐ Já existe o Parceiro “{sit.mesmaRaiz[0].nome}”</strong> com a mesma raiz de CNPJ ({sit.mesmaRaiz[0].cnpjReferencia}
                    ). Para não duplicar, escolha:
                  </p>
                  <fieldset className="opcoes-cartao lista" aria-label="Como cadastrar">
                    {sit.mesmaRaiz.map((p) => (
                      <label key={p.id} className={`opcao-cartao${opcao === p.id ? ' escolhida' : ''}${p.ativo ? '' : ' desabilitada'}`}>
                        <input type="radio" name="opcao-empresa" checked={opcao === p.id} disabled={!p.ativo} onChange={() => setOpcao(p.id)} data-teste="fav-opcao-entidade" />
                        <span>
                          <strong>Adicionar como nova entidade do Parceiro existente</strong>
                          <small>
                            {p.ativo
                              ? `${p.nome} ganha a entidade ${formatarCnpj(digitos)}; a entidade já pode ser escolhida como favorecido; os papéis do Parceiro não mudam`
                              : `${p.nome} está inativo: reative em Parceiros comerciais antes`}
                          </small>
                        </span>
                      </label>
                    ))}
                    <label className={`opcao-cartao${opcao === 'novo' ? ' escolhida' : ''}`}>
                      <input type="radio" name="opcao-empresa" checked={opcao === 'novo'} onChange={() => setOpcao('novo')} data-teste="fav-opcao-novo" />
                      <span>
                        <strong>É outra empresa: cadastrar Parceiro novo</strong>
                        <small>Só se não for o mesmo grupo — pede justificativa</small>
                      </span>
                    </label>
                  </fieldset>
                </>
              ) : (
                <p className="alerta-favorecido">
                  Nenhum Parceiro com este CNPJ nem com a mesma raiz ({digitos.slice(0, 2)}.{digitos.slice(2, 5)}.{digitos.slice(5, 8)}). Será criado{' '}
                  <strong>um Parceiro novo com esta entidade</strong>.
                </p>
              )}
              <div className="grade-form duas">
                {!empresaEmEntidade && (
                  <>
                    <Campo rotulo="Nome do Parceiro">{(id) => <input id={id} value={nomeParceiro} onChange={(e) => setNomeParceiro(e.target.value)} data-teste="fav-nome-parceiro" />}</Campo>
                    <Campo rotulo="Papel">{(id) => <input id={id} value="Fornecedor de despesas" readOnly className="somente-leitura" />}</Campo>
                  </>
                )}
                <Campo rotulo={sit.receita?.encontrado ? 'Razão social (da Receita, editável)' : 'Razão social'}>
                  {(id) => <input id={id} value={razao} onChange={(e) => setRazao(e.target.value)} data-teste="fav-razao" />}
                </Campo>
                <Campo rotulo="UF">
                  {(id) => (
                    <select id={id} value={uf} onChange={(e) => setUf(e.target.value)} data-teste="fav-uf">
                      <option value="">Selecione…</option>
                      {UFS.map((u) => (
                        <option key={u}>{u}</option>
                      ))}
                    </select>
                  )}
                </Campo>
                {!empresaEmEntidade && sit.mesmaRaiz.length > 0 && (
                  <Campo rotulo="Justificativa (é outra empresa)">{(id) => <input id={id} value={justOutra} onChange={(e) => setJustOutra(e.target.value)} data-teste="fav-just-outra" />}</Campo>
                )}
              </div>
              <p className="texto-secundario sem-margem">
                {empresaEmEntidade
                  ? 'Basta a permissão “Criar Parceiro”: a entidade entra no Parceiro existente sem alterar papéis nem outros dados dele.'
                  : 'Mesmas validações de Parceiros comerciais (CNPJ, duplicidade, raiz), com só os campos mínimos. O restante se completa depois em Parceiros comerciais.'}
              </p>
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="grade-form duas">
            <Campo rotulo="Nome">{(id) => <input id={id} value={nome} onChange={(e) => setNome(e.target.value)} data-teste="fav-nome" />}</Campo>
            {tipo === 'pf' ? (
              <Campo rotulo="CPF">{(id) => <input id={id} value={cpf} onChange={(e) => setCpf(e.target.value)} disabled={semDoc} inputMode="numeric" data-teste="fav-cpf" />}</Campo>
            ) : (
              <Campo rotulo={tipo === 'estrangeiro' ? 'Identificação fiscal' : 'Identificação fiscal (opcional)'}>
                {(id) => <input id={id} value={identificacao} onChange={(e) => setIdentificacao(e.target.value)} disabled={semDoc} data-teste="fav-identificacao" />}
              </Campo>
            )}
            {tipo === 'estrangeiro' && <Campo rotulo="País">{(id) => <input id={id} value={pais} onChange={(e) => setPais(e.target.value)} data-teste="fav-pais" />}</Campo>}
            <Campo rotulo="E-mail (opcional)">{(id) => <input id={id} value={email} onChange={(e) => setEmail(e.target.value)} />}</Campo>
            <Campo rotulo="Telefone (opcional)">{(id) => <input id={id} value={telefone} onChange={(e) => setTelefone(e.target.value)} />}</Campo>
          </div>
          <label className="caixa-marcar">
            <input type="checkbox" checked={semDoc} onChange={(e) => setSemDoc(e.target.checked)} data-teste="fav-sem-documento" />
            <span>
              Sem documento
              <small className="bloco texto-secundario">exige justificativa</small>
            </span>
          </label>
          {semDoc && <Campo rotulo="Justificativa (sem documento)">{(id) => <input id={id} value={justSemDoc} onChange={(e) => setJustSemDoc(e.target.value)} data-teste="fav-just-sem-doc" />}</Campo>}
          <p className="texto-secundario sem-margem">
            O mesmo cadastro de Administração › Pessoas financeiras (CPF validado, sem duplicidade). Vínculo com usuário do HUB fica para o cadastro completo.
          </p>
        </>
      )}
    </Modal>
  );
}

/* ====================================================================== origem dos recursos (F02/F06) */
interface DadosPagamento {
  data: string;
  contaId: string;
  meio: string;
  identificador: string;
}
function CamposPagamento({
  v,
  set,
  bancos,
  valor,
  origem,
  setOrigem,
  permitidas,
  parcelas = 1,
  valores = [],
  rotulos,
  valorPago = '',
  erroApi = null,
}: {
  v: DadosPagamento;
  set: (x: DadosPagamento) => void;
  bancos: ContaBancaria[];
  valor?: ReactNode;
  /** A4: "Quem pagou" (conta, cartão ou pessoa); sem ele, só a conta da Inttrix */
  origem?: Origem;
  setOrigem?: (o: Origem) => void;
  permitidas?: Origem['tipo'][];
  parcelas?: number;
  valores?: number[];
  /** várias obrigações pagas juntas (rótulo de cada uma) */
  rotulos?: string[];
  valorPago?: string;
  erroApi?: ErroApi | null;
}) {
  const tipo = origem?.tipo ?? 'conta';
  return (
    <>
      {origem && setOrigem && <EscolhaQuemPagou o={origem} set={setOrigem} permitidas={permitidas} />}
      <div className="grade-form duas">
        <CampoData rotulo={tipo === 'cartao' ? 'Data da compra' : 'Data do pagamento'} valor={v.data} aoMudar={(d) => set({ ...v, data: d })} max={hojeIso()} obrigatorio teste="data-pagamento" />
        {valor}
      </div>
      {tipo === 'cartao' && origem && setOrigem && <CamposCartao o={origem} set={setOrigem} data={v.data} parcelas={parcelas} valores={valores} rotulos={rotulos} />}
      {tipo === 'pessoa' && origem && setOrigem && (
        <>
          <div className="grade-form duas">
            <Campo rotulo="Como a pessoa pagou">
              {(id) => (
                <select id={id} value={v.meio} onChange={(e) => set({ ...v, meio: e.target.value })} data-teste="meio-pessoa">
                  {Object.entries(MEIOS)
                    .filter(([k]) => k !== 'cartao')
                    .map(([k, n]) => (
                      <option key={k} value={k}>
                        {n}
                      </option>
                    ))}
                </select>
              )}
            </Campo>
          </div>
          <CamposPessoa o={origem} set={setOrigem} valorPago={valorPago} bancos={bancos} erroApi={erroApi} />
        </>
      )}
      {tipo === 'conta' && (
        <div className="grade-form duas">
          <Campo rotulo="Conta da Inttrix">
            {(id) => (
              <select id={id} value={v.contaId} onChange={(e) => set({ ...v, contaId: e.target.value })} data-teste="conta-pagamento">
                {bancos.length === 0 && <option value="">Nenhuma conta bancária ativa</option>}
                {bancos.map((b) => (
                  <option key={b.id} value={b.id}>
                    {nomeBanco(b)}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          <Campo rotulo="Meio">
            {(id) => (
              <select id={id} value={v.meio} onChange={(e) => set({ ...v, meio: e.target.value })} data-teste="meio-pagamento">
                {Object.entries(MEIOS)
                  .filter(([k]) => k !== 'cartao')
                  .map(([k, n]) => (
                    <option key={k} value={k}>
                      {n}
                    </option>
                  ))}
              </select>
            )}
          </Campo>
          <Campo
            rotulo="Identificador da transação"
            dica={<Ajuda titulo="Identificador da transação">ID do PIX (E2E), nº do boleto ou da transferência. Não se repete na mesma conta: evita registrar o mesmo pagamento duas vezes.</Ajuda>}
          >
            {(id) => <input id={id} value={v.identificador} onChange={(e) => set({ ...v, identificador: e.target.value })} maxLength={120} data-teste="identificador-pagamento" />}
          </Campo>
        </div>
      )}
    </>
  );
}

function Arquivo({ rotulo, arquivo, setArquivo, teste }: { rotulo: string; arquivo: File | null; setArquivo: (f: File | null) => void; teste: string }) {
  return (
    <div className="quadro-arquivo" data-teste={teste}>
      <span className="rotulo-arquivo">{rotulo}</span>
      <label className="botao-secundario pequeno escolher-arquivo">
        {arquivo ? 'Trocar arquivo' : 'Anexar arquivo'}
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.xml" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} className="rotulo-oculto" data-teste={`${teste}-entrada`} />
      </label>
      <small className="texto-secundario">{arquivo ? arquivo.name : 'pendente'}</small>
    </div>
  );
}

/* ====================================================================== Nova despesa (F02/F03) */
export function NovaDespesa({ aoFechar, aoSalvar, compraCartao = false }: { aoFechar: () => void; aoSalvar: (numero?: string) => void; compraCartao?: boolean }) {
  const { pode } = useSessao();
  const apoio = useApoio();
  const hoje = hojeIso();
  const [tipo, setTipo] = useState<'avulsa' | 'parcelada' | 'recorrente'>('avulsa');
  const [termino, setTermino] = useState<'sem_termino' | 'ocorrencias' | 'competencia'>('sem_termino');
  const [terminoN, setTerminoN] = useState('12');
  const [terminoComp, setTerminoComp] = useState('');
  const [verTodas, setVerTodas] = useState(false);
  const [favorecido, setFavorecido] = useState<Favorecido | null>(null);
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [vencimento, setVencimento] = useState(hoje);
  const [competencia, setCompetencia] = useState(mesBr(hoje.slice(0, 7)));
  const [contaId, setContaId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [parcelas, setParcelas] = useState('3');
  const [intervaloMeses, setIntervaloMeses] = useState('1');
  const [docTipo, setDocTipo] = useState('');
  const [docNumero, setDocNumero] = useState('');
  const [docEmissao, setDocEmissao] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [jaPaga, setJaPaga] = useState(compraCartao);
  const [origem, setOrigem] = useState<Origem>(() => origemInicial(compraCartao ? 'cartao' : 'conta'));
  const [erroApi, setErroApi] = useState<ErroApi | null>(null);
  const [pg, setPg] = useState<DadosPagamento>({
    data: hoje,
    contaId: '',
    meio: 'pix',
    identificador: '',
  });
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [chave] = useState(novaChave);
  const a = apoio.dados;
  // A4: a parcelada só "já paga" no cartão (cada parcela entra na sua fatura)
  const pagaAgora = jaPaga && (tipo === 'avulsa' || (tipo === 'parcelada' && origem.tipo !== 'conta'));
  const pendencia = pagaAgora ? pendenciaOrigem(origem) : null;
  useEffect(() => {
    if (a && !pg.contaId && a.bancos[0]) setPg((x) => ({ ...x, contaId: a.bancos[0].id }));
  }, [a, pg.contaId]);
  const total = centavosDe(valor);
  const n = Number(parcelas);
  const previa =
    tipo === 'parcelada' && Number.isInteger(n) && n >= 2 && n <= 120 && total > 0
      ? dividirParcelas(total, n).map((v, i) => ({
          v,
          venc: somarMeses(vencimento, i * Number(intervaloMeses)),
        }))
      : [];
  // A3: projeção da série (horizonte móvel de 12 meses quando não há término; nunca "1/12")
  const compIso = (() => {
    const m = /^(\d{2})\/(\d{4})$/.exec(competencia.trim());
    return m ? `${m[2]}-${m[1]}-01` : '';
  })();
  const freq = Number(intervaloMeses);
  const projecao: { comp: string; venc: string }[] = [];
  if (tipo === 'recorrente' && compIso && vencimento) {
    const dia = Number(vencimento.slice(8, 10));
    const limite = somarMeses(hoje, 12);
    const fimComp = /^(\d{2})\/(\d{4})$/.exec(terminoComp.trim());
    const ateComp = fimComp ? `${fimComp[2]}-${fimComp[1]}-01` : '';
    for (let k = 0; k < 240; k++) {
      const o = { comp: somarMeses(compIso, k * freq), venc: vencimentoNoMes(vencimento, k * freq, dia) };
      if (termino === 'ocorrencias' && k >= Number(terminoN || 0)) break;
      if (termino === 'competencia' && (!ateComp || o.comp > ateComp)) break;
      if (termino === 'sem_termino' && k > 0 && o.venc > limite) break;
      projecao.push(o);
    }
  }
  const podeJaPaga = pode('registrar', 'pagamento');
  const salvar = async () => {
    setErro('');
    if (!favorecido) return setErro('Escolha o favorecido.');
    setErroApi(null);
    setSalvando(true);
    try {
      const r = await api.post<{ despesa: string; obrigacoes: string[] }>('/api/contas-pagar/despesas', {
        tipo,
        favorecido: { tipo: favorecido.tipo, id: favorecido.id },
        descricao,
        valor,
        vencimento,
        competencia,
        plano_contas_id: contaId,
        centro_custo_id: centroId || null,
        parcelas: tipo === 'parcelada' ? n : undefined,
        intervalo_meses: tipo === 'parcelada' ? Number(intervaloMeses) : undefined,
        frequencia_meses: tipo === 'recorrente' ? freq : undefined,
        termino: tipo === 'recorrente' ? termino : undefined,
        termino_ocorrencias: tipo === 'recorrente' && termino === 'ocorrencias' ? Number(terminoN) : undefined,
        termino_competencia: tipo === 'recorrente' && termino === 'competencia' ? terminoComp : undefined,
        ...(tipo === 'recorrente'
          ? {}
          : {
              documento: { tipo: docTipo || null, numero: docNumero || null, emissao: docEmissao || null },
              estado_documento: confirmado ? 'confirmado' : 'estimado',
              arquivo_documento: await arquivoParaApi(arquivo),
            }),
        ja_paga: pagaAgora,
        pagamento: pagaAgora
          ? {
              data_pagamento: pg.data,
              conta_bancaria_id: origem.tipo === 'conta' ? pg.contaId : undefined,
              meio: origem.tipo === 'cartao' ? undefined : pg.meio,
              identificador_transacao: origem.tipo === 'conta' ? pg.identificador || null : undefined,
              chave_idempotencia: chave,
              ...payloadOrigem(origem, valor),
            }
          : undefined,
      });
      aoSalvar(r.obrigacoes[0]);
    } catch (e) {
      setErro((e as Error).message);
      setErroApi(e instanceof ErroApi ? e : null);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={compraCartao ? 'Nova compra no cartão' : 'Nova despesa'}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={salvando || !a || Boolean(pendencia)} title={pendencia ?? undefined} onClick={() => void salvar()} data-teste="salvar-despesa">
            {tipo === 'recorrente' ? 'Salvar recorrência' : pagaAgora && origem.tipo === 'cartao' ? 'Salvar compra no cartão' : tipo === 'parcelada' ? 'Salvar despesa parcelada' : pagaAgora ? 'Salvar despesa e pagamento' : 'Salvar despesa'}
          </button>
        </>
      }
    >
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="form-despesa" data-teste="form-despesa">
          <fieldset className="opcoes-cartao tres" data-teste="tipo-despesa">
            <legend className="rotulo-oculto">Tipo da despesa</legend>
            {(
              [
                ['avulsa', 'Avulsa', 'Uma obrigação'],
                ['parcelada', 'Parcelada', 'Total dividido em parcelas'],
                ['recorrente', 'Recorrente', 'Repete com valor previsto'],
              ] as const
            ).map(([id, nome, desc]) => (
              <label key={id} className={`opcao-cartao${tipo === id ? ' escolhida' : ''}`}>
                <input type="radio" name="tipo-despesa" checked={tipo === id} onChange={() => (setTipo(id), id === 'recorrente' && setJaPaga(false), id === 'parcelada' && jaPaga && origem.tipo === 'conta' && setOrigem({ ...origem, tipo: 'cartao' }))} data-teste={`tipo-${id}`} />
                <span>
                  <strong>{nome}</strong>
                  <small>{desc}</small>
                </span>
              </label>
            ))}
          </fieldset>
          <section className="quadro-form">
            <div className="grade-form quatro">
              <div className="campo largo-2">
                <span className="rotulo-campo">
                  Favorecido{' '}
                  <Ajuda titulo="Favorecido">
                    Busca unificada: Parceiros comerciais (com CNPJ) e Pessoas financeiras (pessoa física, estrangeiro ou órgão sem CNPJ). Cadastre em Administração › Cadastros base.
                  </Ajuda>
                </span>
                <BuscaFavorecido valor={favorecido} aoEscolher={setFavorecido} />
              </div>
              <Campo rotulo="Descrição">{(id) => <input id={id} value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={200} data-teste="descricao-despesa" />}</Campo>
              <Campo rotulo={tipo === 'parcelada' ? 'Valor total' : tipo === 'recorrente' ? 'Valor previsto por ocorrência' : 'Valor'}>
                {(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" data-teste="valor-despesa" />}
              </Campo>
              {tipo === 'parcelada' && (
                <Campo rotulo="Quantidade de parcelas">
                  {(id) => <input id={id} value={parcelas} onChange={(e) => setParcelas(e.target.value.replace(/\D/g, ''))} inputMode="numeric" data-teste="parcelas-despesa" />}
                </Campo>
              )}
              {tipo === 'recorrente' && (
                <Campo rotulo="Frequência">
                  {(id) => (
                    <select id={id} value={intervaloMeses} onChange={(e) => setIntervaloMeses(e.target.value)} data-teste="frequencia-recorrencia">
                      {Object.entries(FREQUENCIAS).map(([k, n]) => (
                        <option key={k} value={k}>
                          {n}
                        </option>
                      ))}
                    </select>
                  )}
                </Campo>
              )}
              <CampoData rotulo={tipo === 'avulsa' ? 'Vencimento' : 'Primeiro vencimento'} valor={vencimento} aoMudar={setVencimento} obrigatorio teste="vencimento-despesa" />
              {tipo === 'recorrente' && (
                <Campo
                  rotulo="Término"
                  dica={<Ajuda titulo="Término">Sem término: o HUB mantém sempre 12 meses de horizonte projetado (os meses, não 12 ocorrências) e avança sozinho, sem repetir o que já foi criado.</Ajuda>}
                >
                  {(id) => (
                    <select id={id} value={termino} onChange={(e) => setTermino(e.target.value as typeof termino)} data-teste="termino-recorrencia">
                      <option value="sem_termino">Sem término</option>
                      <option value="ocorrencias">Após N ocorrências</option>
                      <option value="competencia">Até a competência</option>
                    </select>
                  )}
                </Campo>
              )}
              {tipo === 'recorrente' && termino === 'ocorrencias' && (
                <Campo rotulo="Número de ocorrências">
                  {(id) => <input id={id} value={terminoN} onChange={(e) => setTerminoN(e.target.value.replace(/\D/g, ''))} inputMode="numeric" data-teste="termino-n" />}
                </Campo>
              )}
              {tipo === 'recorrente' && termino === 'competencia' && (
                <Campo rotulo="Última competência">{(id) => <input id={id} value={terminoComp} onChange={(e) => setTerminoComp(e.target.value)} placeholder="MM/AAAA" maxLength={7} data-teste="termino-competencia" />}</Campo>
              )}
              {tipo === 'parcelada' && (
                <Campo rotulo="Intervalo">
                  {(id) => (
                    <select id={id} value={intervaloMeses} onChange={(e) => setIntervaloMeses(e.target.value)} data-teste="intervalo-despesa">
                      <option value="1">Mensal</option>
                      <option value="2">Bimestral</option>
                      <option value="3">Trimestral</option>
                      <option value="6">Semestral</option>
                      <option value="12">Anual</option>
                    </select>
                  )}
                </Campo>
              )}
              <Campo
                rotulo="Conta gerencial"
                dica={<Ajuda titulo="Conta gerencial">Só aparecem as contas ativas marcadas como “pode ser usada em obrigação” (despesa sem desembolso, como depreciação, não gera obrigação).</Ajuda>}
              >
                {(id) => (
                  <select id={id} value={contaId} onChange={(e) => setContaId(e.target.value)} data-teste="conta-gerencial-despesa">
                    <option value="">Escolha…</option>
                    {a.contasDespesa.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.codigo} — {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo rotulo="Centro de custo">
                {(id) => (
                  <select id={id} value={centroId} onChange={(e) => setCentroId(e.target.value)} data-teste="centro-despesa">
                    <option value="">Sem centro de custo</option>
                    {a.centros.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo
                rotulo={tipo === 'recorrente' ? 'Primeira competência' : 'Competência'}
                dica={
                  tipo === 'recorrente' ? (
                    <Ajuda titulo="Primeira competência">Mês da primeira ocorrência (MM/AAAA), independente do primeiro vencimento — por exemplo, honorários de 10/2026 que vencem em 05/11/2026. As seguintes avançam juntas pela frequência.</Ajuda>
                  ) : (
                    <Ajuda titulo="Competência">Mês a que a despesa pertence (MM/AAAA), independente dos vencimentos. Na parcelada, todas as parcelas ficam na competência da origem.</Ajuda>
                  )
                }
              >
                {(id) => <input id={id} value={competencia} onChange={(e) => setCompetencia(e.target.value)} placeholder="MM/AAAA" maxLength={7} data-teste="competencia-despesa" />}
              </Campo>
            </div>
          </section>
          {tipo === 'recorrente' && (
            <section className="quadro-form" data-teste="projecao-recorrencia">
              <h3>
                {termino === 'sem_termino' ? `Projeção móvel · 12 meses` : `${projecao.length} ocorrências`}{' '}
                <Ajuda titulo="Projeção">
                  Cada ocorrência nasce estimada (Projetado) e é completada na própria ocorrência quando a fatura chegar: nº, emissão, valor efetivo e arquivo — inclusive depois do pagamento. Estender o horizonte nunca repete ocorrências já criadas.
                </Ajuda>
              </h3>
              {projecao.length ? (
                <table className="tabela tabela-compacta">
                  <thead>
                    <tr>
                      <th>Ocorrência</th>
                      <th>Competência</th>
                      <th>Vencimento</th>
                      <th className="numero">Valor previsto</th>
                      <th>Documento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(verTodas ? projecao : projecao.slice(0, 3)).map((p, i) => (
                      <tr key={i}>
                        <td>{ordinal(i + 1)}</td>
                        <td>{mesBr(p.comp.slice(0, 7))}</td>
                        <td>{dataBr(p.venc)}</td>
                        <td className="numero">{total > 0 ? rs(total / 100) : '—'}</td>
                        <td>
                          <span className="selo-projetado">Projetado</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="texto-secundario">Informe a primeira competência (MM/AAAA), o primeiro vencimento e o término.</p>
              )}
              {projecao.length > 3 && (
                <p className="texto-secundario sem-margem">
                  <button type="button" className="botao-link" onClick={() => setVerTodas(!verTodas)} data-teste="ver-todas-ocorrencias">
                    {verTodas ? 'Mostrar só as 3 primeiras' : `Ver as ${projecao.length} ocorrências`}
                  </button>
                  {termino === 'sem_termino' ? ' · no horizonte de 12 meses; estender o horizonte não repete ocorrências já criadas.' : ''}
                </p>
              )}
            </section>
          )}
          {tipo === 'parcelada' && (
            <section className="quadro-form" data-teste="conferir-parcelas">
              <h3>
                Conferir parcelas{' '}
                <Ajuda titulo="Conferir parcelas">A diferença do arredondamento fica na última parcela; o total não muda. A competência é a da origem — as parcelas não viram despesas mensais.</Ajuda>
              </h3>
              {previa.length ? (
                <table className="tabela tabela-compacta">
                  <thead>
                    <tr>
                      <th>Parcela</th>
                      <th>Vencimento</th>
                      <th className="numero">Valor</th>
                      <th>Competência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previa.map((p, i) => (
                      <tr key={i}>
                        <td>
                          {i + 1}/{previa.length}
                        </td>
                        <td>{dataBr(p.venc)}</td>
                        <td className="numero">{rs(p.v / 100)}</td>
                        <td>{competencia} (da origem)</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="linha-total">
                      <td colSpan={2}>
                        <strong>Total</strong>
                      </td>
                      <td className="numero">
                        <strong>{rs(total / 100)}</strong>
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="texto-secundario">Informe o valor total e a quantidade de parcelas (2 a 120).</p>
              )}
            </section>
          )}
          {tipo !== 'recorrente' && (
          <section className="quadro-form" data-teste="documento-despesa">
            <h3>
              Documento da despesa{' '}
              <Ajuda titulo="Documento da despesa">
                Pode ficar para depois: lance com o valor estimado e complete nº, emissão e arquivo quando a fatura chegar — inclusive depois do pagamento, na mesma despesa.
              </Ajuda>
            </h3>
            <div className="grade-form quatro">
              <Campo rotulo="Tipo">
                {(id) => (
                  <select id={id} value={docTipo} onChange={(e) => setDocTipo(e.target.value)} data-teste="tipo-documento">
                    <option value="">Não informado</option>
                    {Object.entries(TIPOS_DOCUMENTO).map(([k, n]) => (
                      <option key={k} value={k}>
                        {n}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo rotulo="Nº do documento">{(id) => <input id={id} value={docNumero} onChange={(e) => setDocNumero(e.target.value)} maxLength={60} data-teste="numero-documento" />}</Campo>
              <CampoData rotulo="Emissão do documento" valor={docEmissao} aoMudar={setDocEmissao} teste="emissao-documento" />
              <label className="caixa-marcar alinhada">
                <input type="checkbox" checked={confirmado} onChange={(e) => setConfirmado(e.target.checked)} data-teste="valor-confirmado" />
                <span>
                  Valor confirmado pelo documento
                  <small className="bloco texto-secundario">sem marcar, fica “estimado”</small>
                </span>
              </label>
            </div>
            {pode('enviar', 'documento_financeiro') && <Arquivo rotulo="Arquivo do documento" arquivo={arquivo} setArquivo={setArquivo} teste="arquivo-documento" />}
          </section>
          )}
          {(tipo === 'avulsa' || tipo === 'parcelada') && (
            <label className={`caixa-destaque${jaPaga ? ' marcada' : ''}${podeJaPaga ? '' : ' desabilitada'}`} title={podeJaPaga ? undefined : 'Seu perfil pode lançar, mas não registrar pagamento.'}>
              <input type="checkbox" checked={jaPaga} disabled={!podeJaPaga} onChange={(e) => (setJaPaga(e.target.checked), tipo === 'parcelada' && origem.tipo === 'conta' && setOrigem({ ...origem, tipo: 'cartao' }))} data-teste="ja-foi-paga" />
              <span>
                <strong>{tipo === 'parcelada' ? 'Já paga no cartão ou por uma pessoa' : 'Já foi paga'}</strong>
                <small>{tipo === 'parcelada' ? 'Cartão: cada parcela na sua fatura · Pessoa: um pagamento quita todas as parcelas (reembolso conforme a escolha)' : 'Registra a obrigação e o pagamento juntos'}</small>
              </span>
            </label>
          )}
          {pagaAgora && (
            <section className="quadro-form" data-teste="pagamento-ja-pago">
              <h3>{origem.tipo === 'cartao' ? 'Compra no cartão' : 'Pagamento'}</h3>
              <CamposPagamento
                v={pg}
                set={setPg}
                bancos={a.bancos}
                origem={origem}
                setOrigem={setOrigem}
                permitidas={tipo === 'parcelada' ? ['cartao', 'pessoa'] : ['conta', 'cartao', 'pessoa']}
                parcelas={tipo === 'parcelada' ? n : 1}
                valores={tipo === 'parcelada' && Number.isInteger(n) && n >= 2 && total > 0 ? dividirParcelas(total, n) : [total > 0 ? total : 0]}
                valorPago={valor}
                erroApi={erroApi}
                valor={<Campo rotulo={origem.tipo === 'cartao' ? 'Valor da compra' : 'Valor pago'}>{(id) => <input id={id} value={valor} readOnly className="somente-leitura" />}</Campo>}
              />
            </section>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-despesa">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ====================================================================== Guia recebida (F07): DAS e demais guias */
function GuiaRecebida({ aoFechar, aoSalvar }: { aoFechar: () => void; aoSalvar: (numero: string) => void }) {
  const { pode } = useSessao();
  const apoio = useApoio();
  const a = apoio.dados;
  const hoje = hojeIso();
  const [favorecido, setFavorecido] = useState<Favorecido | null>(null);
  const [descricao, setDescricao] = useState('');
  const [competencia, setCompetencia] = useState(mesBr(somarMeses(`${hoje.slice(0, 7)}-01`, -1).slice(0, 7)));
  const [valor, setValor] = useState('');
  const [vencimento, setVencimento] = useState('');
  const [numero, setNumero] = useState('');
  const [emissao, setEmissao] = useState('');
  const [contaId, setContaId] = useState('');
  const [centroId, setCentroId] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    setErro('');
    if (!favorecido) return setErro('Escolha o favorecido (o órgão que emitiu a guia).');
    setSalvando(true);
    try {
      const r = await api.post<{ obrigacoes: string[] }>('/api/contas-pagar/despesas', {
        tipo: 'avulsa',
        guia: true,
        favorecido: { tipo: favorecido.tipo, id: favorecido.id },
        descricao: descricao || `Guia ${numero}`,
        competencia,
        valor,
        vencimento,
        plano_contas_id: contaId,
        centro_custo_id: centroId || null,
        documento: { tipo: 'guia', numero, emissao: emissao || null },
        arquivo_documento: await arquivoParaApi(arquivo),
      });
      aoSalvar(r.obrigacoes[0]);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo="Registrar guia recebida"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={salvando || !a} onClick={() => void salvar()} data-teste="salvar-guia">
            Salvar guia
          </button>
        </>
      }
    >
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="form-despesa" data-teste="form-guia">
          <p className="texto-secundario sem-margem">
            Somente a guia efetivamente recebida (DAS e demais).{' '}
            <Ajuda titulo="Guia recebida">O HUB não estima nem gera guia, não cria obrigação mensal automática e não calcula imposto por percentual. A guia nasce com o documento confirmado; o arquivo pode ficar pendente (fica identificado na ficha) e o pagamento é outra etapa.</Ajuda>
          </p>
          <section className="quadro-form">
            <div className="grade-form quatro">
              <div className="campo largo-2">
                <span className="rotulo-campo">
                  Favorecido <Ajuda titulo="Favorecido da guia">Órgão com CNPJ: Parceiros comerciais (papel Fornecedor de despesas). Órgão sem CNPJ: Pessoas financeiras.</Ajuda>
                </span>
                <BuscaFavorecido valor={favorecido} aoEscolher={setFavorecido} />
              </div>
              <Campo rotulo="Competência">{(id) => <input id={id} value={competencia} onChange={(e) => setCompetencia(e.target.value)} placeholder="MM/AAAA" maxLength={7} data-teste="guia-competencia" />}</Campo>
              <Campo rotulo="Valor da guia">{(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder="0,00" data-teste="guia-valor" />}</Campo>
              <div className="campo largo-2">
                <Campo rotulo="Nº da guia">{(id) => <input id={id} value={numero} onChange={(e) => setNumero(e.target.value)} maxLength={60} data-teste="guia-numero" />}</Campo>
              </div>
              <CampoData rotulo="Vencimento" valor={vencimento} aoMudar={setVencimento} obrigatorio teste="guia-vencimento" />
              <CampoData rotulo="Emissão" valor={emissao} aoMudar={setEmissao} teste="guia-emissao" />
              <Campo rotulo="Conta gerencial" dica={<Ajuda titulo="Conta gerencial">Inclui as contas marcadas como “Somente guia recebida” (por exemplo, impostos sobre faturamento).</Ajuda>}>
                {(id) => (
                  <select id={id} value={contaId} onChange={(e) => setContaId(e.target.value)} data-teste="guia-conta">
                    <option value="">Escolha…</option>
                    {a.contasObrigacao.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.codigo} — {c.nome}
                        {c.somente_guia_recebida ? ' (guia)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <Campo rotulo="Centro de custo">
                {(id) => (
                  <select id={id} value={centroId} onChange={(e) => setCentroId(e.target.value)} data-teste="guia-centro">
                    <option value="">Sem centro de custo</option>
                    {a.centros.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
              <div className="campo largo-2">
                <Campo rotulo="Descrição (opcional)">{(id) => <input id={id} value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={200} placeholder={`Guia ${numero || '…'}`} data-teste="guia-descricao" />}</Campo>
              </div>
            </div>
          </section>
          {pode('enviar', 'documento_financeiro') && <Arquivo rotulo="Arquivo da guia" arquivo={arquivo} setArquivo={setArquivo} teste="guia-arquivo" />}
          <p className="faixa-resumo-modal">
            <span>
              Após salvar: Documento <strong>Confirmado</strong>
            </span>
            <span>
              Pagamento <strong>Em aberto</strong>
            </span>
          </p>
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-guia">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ====================================================================== Registrar pagamento (F06 / F06b) */
type Tratamento = '' | 'juros_multa' | 'credito_recuperar' | 'erro';
function RegistrarPagamento({ obrigacoes, aoFechar, aoSalvar }: { obrigacoes: Obrigacao[]; aoFechar: () => void; aoSalvar: () => void }) {
  const { pode } = useSessao();
  const apoio = useApoio();
  const a = apoio.dados;
  const varias = obrigacoes.length > 1;
  const saldoTotal = obrigacoes.reduce((s, o) => s + Math.round(Number(o.saldo) * 100), 0);
  const [valor, setValor] = useState(deCentavos(saldoTotal));
  const [porObrigacao, setPorObrigacao] = useState<Record<string, string>>(() => Object.fromEntries(obrigacoes.map((o) => [o.id, deCentavos(Math.round(Number(o.saldo) * 100))])));
  const [pg, setPg] = useState<DadosPagamento>({
    data: hojeIso(),
    contaId: '',
    meio: 'pix',
    identificador: '',
  });
  const [tratamento, setTratamento] = useState<Tratamento>('');
  const [juros, setJuros] = useState('');
  const [multa, setMulta] = useState('');
  const [contaJuros, setContaJuros] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [comprovante, setComprovante] = useState<File | null>(null);
  const [comDesconto, setComDesconto] = useState(false);
  const [contaDesconto, setContaDesconto] = useState('');
  const [justDesconto, setJustDesconto] = useState('');
  const [erro, setErro] = useState('');
  const [erroApi, setErroApi] = useState<ErroApi | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [chave] = useState(novaChave);
  // A4: "Quem pagou" — uma obrigação por vez no cartão ou por pessoa (sem excedente nem desconto)
  const [origem, setOrigem] = useState<Origem>(() => origemInicial('conta'));
  const naConta = origem.tipo === 'conta';
  const pendencia = naConta ? null : pendenciaOrigem(origem);
  useEffect(() => {
    if (!a) return;
    if (!pg.contaId && a.bancos[0]) setPg((x) => ({ ...x, contaId: a.bancos[0].id }));
    if (!contaJuros) setContaJuros(a.contasAtivas.find((c) => c.codigo === 'FIN-103')?.id ?? '');
  }, [a, pg.contaId, contaJuros]);
  const pago = centavosDe(valor);
  const principal = varias ? obrigacoes.reduce((s, o) => s + Math.min(centavosDe(porObrigacao[o.id] ?? '0') || 0, Math.round(Number(o.saldo) * 100)), 0) : Math.min(pago || 0, saldoTotal);
  const excedente = pago > 0 && naConta ? pago - principal : 0;
  // cartão ou pessoa: cada obrigação até o seu saldo (várias: o valor é a soma do aplicado em cada uma)
  const acimaDoSaldo = !naConta && (varias ? obrigacoes.some((o) => (centavosDe(porObrigacao[o.id] ?? '0') || 0) > Math.round(Number(o.saldo) * 100)) : pago > saldoTotal);
  // desconto negociado para quitar: só uma obrigação por vez; é a diferença exata entre o saldo e o valor pago
  const faltaDesconto = naConta && !varias && pago > 0 && pago < saldoTotal ? saldoTotal - pago : 0;
  const desconto = comDesconto && faltaDesconto > 0 ? faltaDesconto : 0;
  const ativosAntes = obrigacoes.reduce((s, o) => s + Math.round(Number(o.pago) * 100), 0);
  const valorObrig = obrigacoes.reduce((s, o) => s + Math.round(Number(o.valor) * 100), 0);
  const escolherTratamento = (t: Tratamento) => {
    setTratamento(t);
    if (t === 'erro') (setValor(deCentavos(principal)), setTratamento(''));
    if (t === 'juros_multa' && !juros && !multa) setJuros(deCentavos(excedente));
  };
  const salvar = async () => {
    setErro('');
    setErroApi(null);
    setSalvando(true);
    try {
      if (!naConta) {
        await api.post('/api/contas-pagar/pagamentos', {
          data_pagamento: pg.data,
          valor: varias ? deCentavos(principal) : valor,
          meio: origem.tipo === 'pessoa' ? pg.meio : undefined,
          chave_idempotencia: chave,
          alocacoes: varias ? obrigacoes.map((o) => ({ obrigacao_id: o.id, valor: porObrigacao[o.id] })) : [{ obrigacao_id: obrigacoes[0].id }],
          ...payloadOrigem(origem, valor),
          comprovante: await arquivoParaApi(comprovante),
        });
        return aoSalvar();
      }
      await api.post('/api/contas-pagar/pagamentos', {
        data_pagamento: pg.data,
        valor,
        origem: 'conta',
        conta_bancaria_id: pg.contaId,
        meio: pg.meio,
        identificador_transacao: pg.identificador || null,
        chave_idempotencia: chave,
        alocacoes: obrigacoes.map((o) => (varias ? { obrigacao_id: o.id, valor: porObrigacao[o.id] } : { obrigacao_id: o.id })),
        excedente:
          excedente > 0 && tratamento
            ? tratamento === 'juros_multa'
              ? {
                  tipo: 'juros_multa',
                  juros: juros || '0',
                  multa: multa || '0',
                  plano_contas_id: contaJuros,
                  justificativa,
                }
              : { tipo: 'credito_recuperar', justificativa }
            : undefined,
        desconto: desconto > 0 ? { valor: deCentavos(desconto), plano_contas_id: contaDesconto, justificativa: justDesconto } : undefined,
        comprovante: await arquivoParaApi(comprovante),
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
      setErroApi(e instanceof ErroApi ? e : null);
    } finally {
      setSalvando(false);
    }
  };
  const titulo = varias ? `Registrar pagamento — ${obrigacoes.length} obrigações` : `Registrar pagamento — ${obrigacoes[0].numero}`;
  return (
    <Modal
      titulo={titulo}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={salvando || !a || (excedente > 0 && !tratamento) || Boolean(pendencia) || acimaDoSaldo}
            title={excedente > 0 && !tratamento ? 'Diga o que é a diferença antes de registrar.' : (pendencia ?? (acimaDoSaldo ? 'No cartão ou por pessoa, o valor vai até o saldo.' : undefined))}
            onClick={() => void salvar()}
            data-teste="confirmar-pagamento"
          >
            {origem.tipo === 'cartao' ? 'Registrar cobrança no cartão' : origem.tipo === 'pessoa' ? 'Registrar pagamento pela pessoa' : 'Registrar pagamento'}
          </button>
        </>
      }
    >
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="form-pagamento" data-teste="form-pagamento">
          <p className="faixa-resumo-modal" data-teste="resumo-obrigacao">
            <span>
              {varias ? 'Obrigações' : 'Obrigação'} <strong>{rs(valorObrig / 100)}</strong>
            </span>
            <span>
              Pagamentos ativos <strong>{rs(ativosAntes / 100)}</strong>
            </span>
            <span>
              Saldo atual <strong>{rs(saldoTotal / 100)}</strong>
            </span>
          </p>
          {varias && (
            <section className="quadro-form" data-teste="alocacao-obrigacoes">
              <h3>
                Quanto vai para cada obrigação{' '}
                <Ajuda titulo="Alocação">
                  Um pagamento pode quitar várias obrigações (por exemplo, um boleto único). Cada valor fica limitado ao saldo da obrigação; o total pago precisa cobrir a soma.
                </Ajuda>
              </h3>
              <table className="tabela tabela-compacta">
                <thead>
                  <tr>
                    <th>Obrigação</th>
                    <th>Favorecido</th>
                    <th className="numero">Saldo</th>
                    <th className="numero">Valor neste pagamento</th>
                  </tr>
                </thead>
                <tbody>
                  {obrigacoes.map((o) => (
                    <tr key={o.id}>
                      <td>
                        {o.numero}
                        <small className="bloco texto-secundario">{o.descricao}</small>
                      </td>
                      <td>{o.favorecido.nome}</td>
                      <td className="numero">{rs(o.saldo)}</td>
                      <td className="numero">
                        <input
                          className="valor-alocacao"
                          value={porObrigacao[o.id]}
                          onChange={(e) =>
                            setPorObrigacao({
                              ...porObrigacao,
                              [o.id]: e.target.value,
                            })
                          }
                          inputMode="decimal"
                          aria-label={`Valor para ${o.numero}`}
                          data-teste={`alocacao-${o.numero}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          <CamposPagamento
            v={pg}
            set={setPg}
            bancos={a.bancos}
            origem={origem}
            setOrigem={(o) => (setOrigem(o), setTratamento(''), setComDesconto(false))}
            valores={varias ? obrigacoes.map((o) => centavosDe(porObrigacao[o.id] ?? '0') || 0) : [pago > 0 ? pago : 0]}
            parcelas={obrigacoes.length}
            rotulos={varias ? obrigacoes.map((o) => (o.tipo === 'parcelada' ? `${o.numero} · ${o.parcela}/${o.parcelas}` : o.numero)) : undefined}
            valorPago={valor}
            erroApi={erroApi}
            valor={
              <Campo rotulo={origem.tipo === 'cartao' ? 'Valor da compra' : 'Valor do pagamento'}>
                {(id) =>
                  varias && !naConta ? (
                    <input id={id} value={deCentavos(principal)} readOnly className="somente-leitura" data-teste="valor-pagamento" />
                  ) : (
                    <input id={id} value={valor} onChange={(e) => (setValor(e.target.value), setTratamento(''))} inputMode="decimal" data-teste="valor-pagamento" />
                  )
                }
              </Campo>
            }
          />
          {excedente > 0 && (
            <>
              <div className="aviso aviso-atencao-fin" role="status" data-teste="aviso-excedente">
                <strong>◐ Atenção:</strong> o valor passa o saldo em {rs(excedente / 100)}.
                <ul>
                  <li>Diga o que é a diferença antes de registrar.</li>
                </ul>
              </div>
              <fieldset className="opcoes-cartao lista" data-teste="tratamento-excedente">
                <legend>
                  A diferença de {rs(excedente / 100)} é{' '}
                  <Ajuda titulo="Diferença">
                    Nada é gravado em silêncio: juros e multa ficam discriminados e justificados na conta gerencial escolhida; pago a maior vira crédito a recuperar com o favorecido (conta CRD-001),
                    em aberto até a recuperação.
                  </Ajuda>
                </legend>
                {(
                  [
                    ['juros_multa', 'Juros e multa cobrados', 'discriminados e justificados'],
                    ['credito_recuperar', 'Pagamento indevido a maior', 'fica como crédito a recuperar com o favorecido'],
                    ['erro', 'Erro de digitação', 'corrigir o valor'],
                  ] as const
                ).map(([id, nome, desc]) => (
                  <label key={id} className={`opcao-cartao${tratamento === id ? ' escolhida' : ''}`}>
                    <input type="radio" name="tratamento" checked={tratamento === id} onChange={() => escolherTratamento(id)} data-teste={`tratamento-${id}`} />
                    <span>
                      <strong>{nome}</strong>
                      <small>{desc}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
              {tratamento === 'juros_multa' && (
                <div className="grade-form duas">
                  <Campo rotulo="Juros">{(id) => <input id={id} value={juros} onChange={(e) => setJuros(e.target.value)} inputMode="decimal" data-teste="juros" />}</Campo>
                  <Campo rotulo="Multa">{(id) => <input id={id} value={multa} onChange={(e) => setMulta(e.target.value)} inputMode="decimal" data-teste="multa" />}</Campo>
                  <Campo rotulo="Conta gerencial">
                    {(id) => (
                      <select id={id} value={contaJuros} onChange={(e) => setContaJuros(e.target.value)} data-teste="conta-juros">
                        <option value="">Escolha…</option>
                        {a.contasAtivas.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.codigo} — {c.nome}
                          </option>
                        ))}
                      </select>
                    )}
                  </Campo>
                </div>
              )}
              {(tratamento === 'juros_multa' || tratamento === 'credito_recuperar') && (
                <Campo rotulo="Justificativa">
                  {(id) => <input id={id} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={500} data-teste="justificativa-excedente" />}
                </Campo>
              )}
            </>
          )}
          {faltaDesconto > 0 && (
            <section className="quadro-form" data-teste="desconto-quitacao">
              <label className={`caixa-destaque${comDesconto ? ' marcada' : ''}`}>
                <input type="checkbox" checked={comDesconto} onChange={(e) => setComDesconto(e.target.checked)} data-teste="com-desconto" />
                <span>
                  <strong>Desconto para quitação</strong>
                  <small>Sem marcar, o pagamento fica parcial e o saldo de {rs(faltaDesconto / 100)} continua em aberto</small>
                </span>
              </label>
              {comDesconto && (
                <div className="grade-form duas">
                  <Campo rotulo="Desconto negociado" dica={<Ajuda titulo="Desconto">O valor original da obrigação não muda: o desconto fica registrado à parte, com conta e justificativa, ligado a este pagamento. Estornar o pagamento desfaz o desconto.</Ajuda>}>
                    {(id) => <input id={id} value={deCentavos(faltaDesconto)} readOnly className="somente-leitura" data-teste="valor-desconto" />}
                  </Campo>
                  <Campo rotulo="Conta gerencial do desconto">
                    {(id) => (
                      <select id={id} value={contaDesconto} onChange={(e) => setContaDesconto(e.target.value)} data-teste="conta-desconto">
                        <option value="">Escolha…</option>
                        {a.contasAtivas.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.codigo} — {c.nome}
                          </option>
                        ))}
                      </select>
                    )}
                  </Campo>
                  <div className="largura-total">
                    <Campo rotulo="Justificativa do desconto">
                      {(id) => <input id={id} value={justDesconto} onChange={(e) => setJustDesconto(e.target.value)} maxLength={500} data-teste="justificativa-desconto" />}
                    </Campo>
                  </div>
                </div>
              )}
            </section>
          )}
          {pode('enviar', 'documento_financeiro') && <Arquivo rotulo="Comprovante de pagamento" arquivo={comprovante} setArquivo={setComprovante} teste="comprovante-pagamento" />}
          {acimaDoSaldo && (
            <p className="aviso aviso-atencao-fin" role="status" data-teste="aviso-acima-saldo">
              No cartão ou por pessoa, o pagamento vai até o saldo ({rs(saldoTotal / 100)}). Juros e encargos do cartão entram na fatura (“Informar encargo ou crédito”).
            </p>
          )}
          {pago > 0 && (
            <section className="quadro-depois" data-teste="apos-registrar">
              <h3>Após registrar</h3>
              <p>
                <span>
                  {principal + desconto >= saldoTotal ? 'Obrigação quitada' : 'Pagamento parcial'} <strong>{rs(principal / 100)}</strong>
                </span>
                {desconto > 0 && (
                  <span>
                    Desconto <strong>{rs(desconto / 100)}</strong>
                  </span>
                )}
                {excedente > 0 && tratamento === 'juros_multa' && (
                  <>
                    <span>
                      Juros <strong>{rs((centavosDe(juros) || 0) / 100)}</strong>
                    </span>
                    <span>
                      Multa <strong>{rs((centavosDe(multa) || 0) / 100)}</strong>
                    </span>
                  </>
                )}
                {excedente > 0 && tratamento === 'credito_recuperar' && (
                  <span>
                    Crédito a recuperar <strong>{rs(excedente / 100)}</strong>
                  </span>
                )}
                <span>
                  Saldo <strong>{rs(Math.max(saldoTotal - principal - desconto, 0) / 100)}</strong>
                </span>
              </p>
            </section>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-pagamento">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ====================================================================== Ficha da obrigação (F05) */
export function FichaObrigacao({ numero }: { numero: string }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<Ficha>(`/api/contas-pagar/obrigacoes/${encodeURIComponent(numero)}`), [numero]);
  const periodosCarga = useCarga(() => api.get<Periodo[]>('/api/financeiro/periodos'), [numero]);
  const [modal, setModal] = useState<'' | 'pagar' | 'documento' | 'cancelar' | 'editar' | 'alterar-serie' | 'encerrar-serie' | 'reprogramar'>('');
  const [estornar, setEstornar] = useState<Pagamento | null>(null);
  const [anexarEm, setAnexarEm] = useState<Pagamento | null>(null);
  const [retirar, setRetirar] = useState<Anexo | null>(null);
  // 0051: recuperação em dinheiro do crédito a recuperar
  const [recuperar, setRecuperar] = useState<{ obrigacao: string; saldo: string } | null>(null);
  const [estornarRcp, setEstornarRcp] = useState<string | null>(null);
  // download que falhou (ex.: arquivo indisponível): a mensagem do servidor aparece ao lado do próprio anexo
  const [erroArquivo, setErroArquivo] = useState<{ id: string; mensagem: string } | null>(null);
  const f = carga.dados;
  useRotuloTrilha(f ? f.obrigacao.numero : null);
  const fechados = useMemo(() => new Set((periodosCarga.dados ?? []).filter((p) => p.fechado).map((p) => p.mes)), [periodosCarga.dados]);
  if (!f) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ficha-pagar" />;
  const o = f.obrigacao;
  const d = f.despesa;
  const recarregar = () => (setModal(''), setEstornar(null), setAnexarEm(null), setRetirar(null), setRecuperar(null), setEstornarRcp(null), void carga.recarregar(), void periodosCarga.recarregar());
  const podeRetirar = pode('enviar', 'documento_financeiro');
  const retirados = f.anexos.filter((x) => x.retirado_em);
  const ativos = f.pagamentos.filter((p) => !p.estorno);
  // o card "Pago" é desta obrigação: conta só os pagamentos ativos aplicados a ela (a tabela continua com os da despesa)
  const ativosDesta = ativos.filter((p) => p.alocacoes.some((a) => a.obrigacao === o.numero));
  const temPagamento = ativos.length > 0;
  const hoje = hojeIso();
  const diasPara = Math.round((Date.parse(o.vencimento) - Date.parse(hoje)) / 86_400_000);
  const vencTexto = o.cancelada
    ? 'cancelada'
    : Number(o.saldo) === 0
      ? 'quitada'
      : o.vencida
        ? `vencida há ${plural(o.diasAtraso, 'dia', 'dias')}`
        : o.venceHoje
          ? 'vence hoje'
          : diasPara === 1
            ? 'vence amanhã'
            : `em ${diasPara} dias`;
  const baixar = async (x: Anexo) => {
    setErroArquivo(null);
    let r: { nome: string; conteudo_base64: string };
    try {
      r = await api.get<{ nome: string; conteudo_base64: string }>(`/api/contas-pagar/anexos/${x.id}/arquivo`);
    } catch (e) {
      setErroArquivo({ id: x.id, mensagem: (e as Error).message });
      return;
    }
    const bytes = Uint8Array.from(atob(r.conteudo_base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes as BlobPart]));
    const el = document.createElement('a');
    el.href = url;
    el.download = r.nome;
    el.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const anexosDoc = f.anexos.filter((x) => x.papel === 'documento' && !x.retirado_em);
  const comprovantesDe = (p: Pagamento) => f.anexos.filter((x) => x.papel === 'comprovante' && x.pagamento_id === p.id && !x.retirado_em);
  return (
    <>
      <section className="resumo-destaque cabecalho-obrigacao" data-teste="cabecalho-obrigacao">
        <div className="cabecalho-registro">
          <div>
            <h1 className="titulo-pagina">
              {o.numero} · {o.descricao}
            </h1>
            <p className="texto-secundario sem-margem" data-teste="origem-obrigacao">
              {o.favorecido.nome}
              {o.favorecido.documento ? ` · ${o.favorecido.documento}` : ''} · Despesa {d.numero}
              {d.tipo === 'parcelada' ? ` · parcela ${o.parcela}/${o.parcelas}` : ''}
              {d.cancelada ? ` · cancelada em ${dataBr(d.cancelada.em)} (motivo: ${d.cancelada.motivo})` : ''}
            </p>
          </div>
          <div className="acoes-direita barra-acoes-registro">
            <span className="selo-tipo-fin">{d.tipo === 'parcelada' ? 'Parcelada' : d.tipo === 'recorrente' ? `Recorrente ${f.recorrencia?.frequencia ?? ''}` : 'Avulsa'}</span>
            {!d.cancelada && pode('editar', 'conta_pagar') && (
              <button type="button" className="botao-secundario" onClick={() => setModal('editar')} data-teste="editar-despesa">
                Editar
              </button>
            )}
            {!d.cancelada && pode('cancelar', 'conta_pagar') && !temPagamento && (
              <button type="button" className="botao-secundario" onClick={() => setModal('cancelar')} data-teste="cancelar-despesa">
                Cancelar despesa
              </button>
            )}
            {!d.cancelada && pode('confirmar_documento', 'conta_pagar') && (
              <button type="button" className="botao-secundario" onClick={() => setModal('documento')} data-teste="completar-documento">
                Completar documento
              </button>
            )}
            {f.podeReprogramar && Number(o.saldo) > 0 && !d.periodoFechado && (
              <button type="button" className="botao-secundario" onClick={() => setModal('reprogramar')} data-teste="reprogramar-vencimento">
                Reprogramar vencimento
              </button>
            )}
            {!o.cancelada && Number(o.saldo) > 0 && pode('registrar', 'pagamento') && (
              <button type="button" className="botao-primario" onClick={() => setModal('pagar')} data-teste="registrar-pagamento">
                Registrar pagamento
              </button>
            )}
          </div>
        </div>
      </section>
      <CardsLista n={4} teste="cards-obrigacao">
        <CardLista icone="documento" tom="azul" rotulo="Valor" valor={rs(o.valor)} detalhe={d.estadoDocumento === 'confirmado' ? 'documento confirmado' : 'previsto (estimado)'} teste="card-valor" />
        <CardLista icone="dinheiro" tom="verde" rotulo="Pago" valor={rs(o.pago)} detalhe={`${plural(ativosDesta.length, 'pagamento ativo', 'pagamentos ativos')}${Number(o.desconto) > 0 ? ` · desconto ${rs(o.desconto)}` : ''}`} teste="card-pago" />
        <CardLista icone="moedas" tom="roxo" rotulo="Saldo" valor={rs(o.saldo)} detalhe="calculado" teste="card-saldo" />
        <CardLista
          icone="relogio"
          tom="azul"
          rotulo="Vencimento"
          valor={dataBr(o.vencimento)}
          detalhe={f.vencimento && f.vencimento.original && f.vencimento.original !== o.vencimento ? `${vencTexto} · original ${dataBr(f.vencimento.original)} (${plural(f.vencimento.reprogramacoes.length, 'reprogramação', 'reprogramações')})` : vencTexto}
          teste="card-vencimento"
        />
      </CardsLista>
      {f.vencimento && f.vencimento.reprogramacoes.length > 0 && (
        <details className="fech-detalhe" data-teste="historico-vencimento">
          <summary>
            Vencimento original {dataBr(f.vencimento.original)} · {plural(f.vencimento.reprogramacoes.length, 'reprogramação', 'reprogramações')}
          </summary>
          <ul className="conc-efeitos">
            {f.vencimento.reprogramacoes.map((r) => (
              <li key={r.em}>
                {dataBr(r.anterior)} → <strong>{dataBr(r.novo)}</strong> em {new Date(r.em).toLocaleString('pt-BR')}
                {r.por ? ` por ${r.por}` : ''}
                {r.motivo ? ` — “${r.motivo}”` : ''}
                {r.fonte === 'auditoria' ? ' (da auditoria: edição anterior ao registro de reprogramação)' : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
      <p className="eixos-obrigacao" data-teste="eixos-obrigacao">
        <span>
          Documento {o.projetado ? <span className="selo-projetado">Projetado</span> : <SeloDocumento estado={d.estadoDocumento} />}
        </span>
        <span>
          Pagamento <SeloPagamento o={o} />
        </span>
        <span>
          Conciliação{' '}
          <span className="texto-secundario" data-teste="conciliacao-ficha">
            {!ativos.some((p) => p.origem === 'conta') ? '—' : ativos.filter((p) => p.origem === 'conta').every((p) => p.conciliacao === 'conciliado') ? 'Conciliado' : 'Não conciliado'}
          </span>
        </span>
        <Ajuda titulo="Três eixos">
          Documento, pagamento e conciliação são situações independentes. Pagamento pela conta da Inttrix fica “Não conciliado” até ser vinculado ao movimento do extrato na Conciliação bancária (cobrança no cartão e pagamento feito por pessoa não passam pelo extrato).
        </Ajuda>
      </p>
      {d.periodoFechado && (
        <Estado
          tipo="bloqueado"
          titulo={`competência ${mesExtenso(d.competencia)} está fechada`}
          teste="periodo-fechado-ficha"
          itens={[
            'Valor, classificação e pagamentos deste mês não mudam.',
            'Nº, tipo e emissão do documento ainda podem ser completados.',
            'Para corrigir: reabra o período (permissão “Reabrir período”, com justificativa) e corrija o próprio registro — sem nova despesa e sem mudar a competência. Depois, feche de novo.',
          ]}
        />
      )}
      <div className={`ficha-pagar-grade${f.recorrencia ? ' com-lateral' : ''}`}>
        <div className="ficha-pagar-principal">
          <section className="cartao destaque-esquerda" data-teste="classificacao">
            <h2>Classificação</h2>
            <dl className="grade-dados quatro">
              <div>
                <dt>Competência</dt>
                <dd>{mesBr(d.competencia)}</dd>
              </div>
              <div>
                <dt>Conta gerencial</dt>
                <dd>
                  {o.conta.codigo} — {o.conta.nome}
                </dd>
              </div>
              <div>
                <dt>Centro de custo</dt>
                <dd>{o.centro ? o.centro.nome : '—'}</dd>
              </div>
              <div>
                <dt>Favorecido</dt>
                <dd>{o.favorecido.nome}</dd>
              </div>
            </dl>
          </section>
          <section className="cartao" data-teste="documento-obrigacao">
            <h2>
              Documento da despesa{' '}
              <Ajuda titulo="Documento da despesa">
                O documento pertence à despesa. Pode ser completado a qualquer momento — inclusive depois do pagamento — sem criar outra despesa. Valor diferente depois do pagamento não muda em
                silêncio.
              </Ajuda>
            </h2>
            {d.estadoDocumento === 'estimado' && (
              <p className="aviso aviso-info" data-teste="documento-estimado">
                Documento ainda não confirmado. Você pode pagar agora e completar depois — na mesma despesa.
              </p>
            )}
            <dl className="grade-dados quatro">
              <div>
                <dt>Tipo</dt>
                <dd>{o.documento.tipo ? TIPOS_DOCUMENTO[o.documento.tipo] : <span className="texto-secundario">não informado</span>}</dd>
              </div>
              <div>
                <dt>Nº do documento</dt>
                <dd data-teste="numero-documento-ficha">{o.documento.numero ?? <span className="texto-secundario">não informado</span>}</dd>
              </div>
              <div>
                <dt>Emissão</dt>
                <dd>{dataBr(o.documento.emissao)}</dd>
              </div>
              <div>
                <dt>Valor estimado · do documento</dt>
                <dd>
                  {rs(d.valorEstimado)} · {d.valorDocumento ? rs(d.valorDocumento) : <span className="texto-secundario">—</span>}
                </dd>
              </div>
            </dl>
            <div className="arquivos-documento" data-teste="arquivos-documento">
              <strong>Arquivo do documento</strong>
              {anexosDoc.length ? (
                <ul className="lista-arquivos">
                  {anexosDoc.map((x) => (
                    <li key={x.id}>
                      {pode('visualizar', 'documento_financeiro') ? (
                        <button type="button" className="botao-link" onClick={() => void baixar(x)} data-teste={`baixar-${x.nome_original}`}>
                          {x.nome_original}
                        </button>
                      ) : (
                        x.nome_original
                      )}
                      {erroArquivo?.id === x.id && (
                        <span className="aviso aviso-erro bloco" role="alert" data-teste="erro-arquivo">
                          {erroArquivo.mensagem}
                        </span>
                      )}
                      <small className="texto-secundario">
                        {' '}
                        · enviado em {dataBr(x.criado_em)}
                        {x.enviado_por ? ` por ${x.enviado_por}` : ''}
                      </small>
                      {podeRetirar && (
                        <button type="button" className="botao-link pequeno retirar-anexo" onClick={() => setRetirar(x)} data-teste={`retirar-${x.nome_original}`}>
                          Retirar
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="texto-secundario"> pendente</span>
              )}
            </div>
          </section>
          {d.tipo === 'parcelada' && (
            <section className="cartao" data-teste="parcelas-despesa">
              <h2>Parcelas da despesa {d.numero}</h2>
              <div className="rolagem">
                <table className="tabela tabela-compacta">
                  <thead>
                    <tr>
                      <th>Parcela</th>
                      <th>Obrigação</th>
                      <th>Vencimento</th>
                      <th>Competência</th>
                      <th className="numero">Valor</th>
                      <th className="numero">Saldo</th>
                      <th>Pagamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.parcelas.map((p) => (
                      <tr key={p.id} className={p.id === o.id ? 'linha-atual' : undefined}>
                        <td>
                          {p.parcela}/{p.parcelas}
                        </td>
                        <td>{p.id === o.id ? <strong>{p.numero}</strong> : <a href={`#/contas-pagar/${p.numero}`}>{p.numero}</a>}</td>
                        <td>{dataBr(p.vencimento)}</td>
                        <td>{mesBr(p.competencia)} (da origem)</td>
                        <td className="numero" data-teste={`valor-vigente-${p.parcela}`}>
                          {rs(p.valor)}
                          {p.valorAntesDocumento && p.valorAntesDocumento !== p.valor && <small className="bloco texto-secundario">antes do documento {rs(p.valorAntesDocumento)}</small>}
                        </td>
                        <td className="numero">{rs(p.saldo)}</td>
                        <td>
                          <SeloPagamento o={p} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {f.redistribuicoes.length > 0 && <ResumoVigente parcelas={f.parcelas} />}
            </section>
          )}
          <section className="cartao" data-teste="pagamentos-obrigacao">
            <h2>Pagamentos</h2>
            {f.pagamentos.length === 0 ? (
              <p className="texto-secundario">Nenhum pagamento registrado.</p>
            ) : (
              <div className="rolagem">
                <table className="tabela tabela-compacta tabela-pagamentos" data-teste="tabela-pagamentos">
                  <thead>
                    <tr>
                      <th>Pagamento</th>
                      <th>Conta · meio</th>
                      <th>Composição</th>
                      <th className="numero">Valor</th>
                      <th>Situação</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.pagamentos.map((p) => {
                      const mesFechado = fechados.has(p.data.slice(0, 7));
                      return (
                        <tr key={p.id} className={p.estorno ? 'linha-estornada' : undefined} data-teste={`pagamento-${p.numero}`}>
                          <td className="pag-coluna-pagamento">
                            <strong className="codigo">{p.numero}</strong>
                            <small className="bloco texto-secundario sem-quebra">{dataBr(p.data)}</small>
                            {p.identificador && <small className="bloco texto-secundario pag-identificador">{p.identificador}</small>}
                          </td>
                          <td>
                            {p.cartao ? (
                              <>
                                {p.cartao.apelido} · final {p.cartao.final}
                                <small className="bloco texto-secundario" data-teste={`fatura-de-${p.numero}`}>
                                  {p.cartao.fatura ? `${p.cartao.fatura.numero} · ${mesExtenso(p.cartao.fatura.mes)} · ${p.cartao.fatura.situacao === 'sugerida' ? 'sugerida · a conferir' : 'conferida'}` : 'sem fatura'}
                                </small>
                              </>
                            ) : p.pessoa ? (
                              <>
                                Pessoa: {p.pessoa.nome}
                                <small className="bloco texto-secundario" data-teste={`reembolso-de-${p.numero}`}>
                                  {p.pessoa.escolha === 'sem' ? `não há valor a reembolsar · ${p.pessoa.justificativa ?? ''}` : p.pessoa.reembolso ? `reembolso ${p.pessoa.reembolso}` : 'gera reembolso (a reembolsar)'}
                                </small>
                              </>
                            ) : (
                              <>
                                {p.conta ? <TextoPartes texto={p.conta} /> : '—'}
                                <small className="bloco texto-secundario">{MEIOS[p.meio] ?? p.meio}</small>
                              </>
                            )}
                          </td>
                          <td>
                            <ul className="lista-composicao">
                              {p.alocacoes.map((x, i) => (
                                <li key={i}>
                                  {ROTULO_ALOCACAO[x.tipo]} {rs(x.valor)}
                                  {x.obrigacao !== o.numero && x.tipo === 'principal' ? ` · ${x.obrigacao}` : ''}
                                  {x.justificativa && x.justificativa !== p.alocacoes[i + 1]?.justificativa && <small className="bloco texto-secundario">{x.justificativa}</small>}
                                </li>
                              ))}
                            </ul>
                            {comprovantesDe(p).map((x) => (
                              <span key={x.id} className="bloco">
                                <button type="button" className="botao-link pequeno" onClick={() => void baixar(x)}>
                                  Comprovante: {x.nome_original}
                                </button>
                                {erroArquivo?.id === x.id && (
                                  <span className="aviso aviso-erro bloco" role="alert" data-teste="erro-arquivo">
                                    {erroArquivo.mensagem}
                                  </span>
                                )}
                                {podeRetirar && (
                                  <button type="button" className="botao-link pequeno retirar-anexo" onClick={() => setRetirar(x)} data-teste={`retirar-${x.nome_original}`}>
                                    Retirar
                                  </button>
                                )}
                              </span>
                            ))}
                          </td>
                          <td className="numero">{rs(p.valor)}</td>
                          <td>
                            {p.estorno ? (
                              <>
                                <SeloSituacao texto="Estornado" tom="encerrado" />
                                <small className="bloco texto-secundario">
                                  {dataBr(p.estorno.em)} por {p.estorno.por}: {p.estorno.justificativa}
                                </small>
                              </>
                            ) : (
                              <SeloSituacao texto="Ativo" tom="positivo" />
                            )}
                            <small className="bloco texto-secundario pag-conciliacao" data-teste={`conciliacao-${p.numero}`}>
                              {p.origem !== 'conta' ? (
                                'Conciliação: não se aplica'
                              ) : p.conciliacao === 'conciliado' ? (
                                <>
                                  Conciliado{p.conciliacaoNumero ? ' · ' : ''}
                                  {p.conciliacaoNumero && <Codigo>{p.conciliacaoNumero}</Codigo>}
                                </>
                              ) : (
                                'Não conciliado'
                              )}
                            </small>
                          </td>
                          <td>
                            <div className="acoes-linha acoes-pagamento">
                              {!p.estorno && pode('enviar', 'documento_financeiro') && comprovantesDe(p).length === 0 && (
                                <button type="button" className="botao-secundario pequeno" onClick={() => setAnexarEm(p)} data-teste={`anexar-comprovante-${p.numero}`}>
                                  Anexar comprovante
                                </button>
                              )}
                              {!p.estorno && pode('estornar', 'pagamento') && (
                                <button
                                  type="button"
                                  className="botao-secundario pequeno"
                                  onClick={() => setEstornar(p)}
                                  title={mesFechado ? `${mesExtenso(p.data.slice(0, 7))} está fechado` : undefined}
                                  data-teste={`estornar-${p.numero}`}
                                >
                                  Estornar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {f.creditosARecuperar.length > 0 && (
              <div className="aviso aviso-info" data-teste="creditos-recuperar">
                <strong>Crédito a recuperar com o favorecido</strong>
                <Ajuda titulo="Crédito a recuperar">
                  Nasce da confirmação do documento abaixo do que foi pago ou de pagamento indevido a maior — o pagamento original não muda. Recupera-se por devolução na fatura do cartão ou em dinheiro (depósito do fornecedor); os dois usam o mesmo saldo e nunca passam dele. Desfazer a conciliação da recuperação não é estorno: a recuperação fica, aguardando extrato. Estornar corrige um registro incorreto e devolve o saldo.
                </Ajuda>
                {f.creditosARecuperar.map((c) => (
                  <div key={c.obrigacao} className="credito-recuperacao" data-teste={`credito-${c.obrigacao}`}>
                    <div>
                      <Codigo>{c.obrigacao}</Codigo> · gerado <strong>{rs(c.gerado)}</strong> (<ComCodigos texto={c.origens.map((x) => (x.tipo === 'documento' ? `documento confirmado abaixo do pago${c.pagamentosOrigem.length ? ` · parte de ${c.pagamentosOrigem.join(', ')} (não é saída nova)` : ''}` : `pago a maior${x.pagamento ? ` em ${x.pagamento}` : ''}`)).join(' + ')} />) · recuperado <strong>{rs(c.recuperado)}</strong> · saldo a recuperar <strong>{rs(c.saldo)}</strong>{' '}
                      {Number(c.saldo) === 0 && <SeloSituacao texto="Recuperado" tom="positivo" />}
                      {c.justificativa && <span className="texto-secundario"> — {c.justificativa}</span>}
                    </div>
                    {c.recuperacoes.length > 0 && (
                      <ul data-teste={`recuperacoes-${c.obrigacao}`}>
                        {c.recuperacoes.map((r) => (
                          <li key={r.numero + r.em} className={r.estorno ? 'texto-secundario' : undefined}>
                            {r.tipo === 'fatura' ? (
                              <>
                                devolução na fatura{' '}
                                <button type="button" className="botao-link" onClick={() => abrirFatura(r.fatura!)} data-teste={`fatura-recuperacao-${r.fatura}`}>
                                  {r.fatura}
                                </button>{' '}
                                · {mesExtenso(r.mes!)} · {rs(r.valor)}
                              </>
                            ) : (
                              <span className="rcp-acoes" data-teste={`rcp-${r.numero}`}>
                                <span>
                                  <strong className="codigo">{r.numero}</strong> · em dinheiro · {dataBr(r.data!)} · {r.conta} · {rs(r.valor)} ·{' '}
                                  {r.estorno ? (
                                    `estornada — “${r.estorno.justificativa}”`
                                  ) : r.conciliacao ? (
                                    <SeloSituacao texto={`Conciliado · ${r.conciliacao}${r.movimentos ? ` · ${r.movimentos}` : ''}`} tom="positivo" />
                                  ) : (
                                    <span className="texto-secundario">aguardando extrato</span>
                                  )}
                                </span>
                                {!r.estorno && c.podeEstornarRecuperacao && (r.conciliacao ? <span className="texto-secundario" data-teste={`rcp-estorno-bloqueado-${r.numero}`}>estornar: desfaça {r.conciliacao} antes</span> : (
                                  <button type="button" className="botao-link" onClick={() => setEstornarRcp(r.numero)} data-teste={`estornar-rcp-${r.numero}`}>
                                    Estornar registro
                                  </button>
                                ))}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {c.podeRegistrarRecuperacao && (
                      <button type="button" className="botao-link" onClick={() => setRecuperar({ obrigacao: c.obrigacao, saldo: c.saldo })} data-teste={`registrar-recuperacao-${c.obrigacao}`}>
                        Registrar recuperação recebida
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {f.ajustes.length > 0 && (
              <div className="ajustes-obrigacao" data-teste="ajustes-obrigacao">
                <strong>Ajustes da obrigação</strong>
                <ul className="lista-arquivos">
                  {f.ajustes.map((x) => (
                    <li key={x.id} className={x.estornado ? 'ajuste-estornado' : undefined}>
                      {rotuloAjuste(x)} <strong>{rs(x.valor)}</strong> · {x.conta}
                      {x.pagamento ? ` · ${x.pagamento}` : ''} · {dataBr(x.em)}
                      {x.autor ? ` por ${x.autor}` : ''} — {x.justificativa}
                      {x.estornado && <SeloSituacao texto="Desfeito pelo estorno" tom="encerrado" />}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
          {f.redistribuicoes.length > 0 && (
            <section className="cartao" data-teste="redistribuicoes">
              <h2>Valor do documento nas parcelas</h2>
              {f.redistribuicoes.map((r) => (
                <div key={r.id} className="bloco-redistribuicao">
                  <p className="sem-margem">
                    {dataBr(r.em)}
                    {r.autor ? ` por ${r.autor}` : ''}: de <strong>{rs(r.valorAnterior)}</strong> para <strong>{rs(r.valorDocumento)}</strong>
                    {Number(r.credito) > 0 ? ` · crédito a recuperar ${rs(r.credito)}` : ''} — {r.justificativa}
                    {r.justificativaCredito ? ` · crédito: ${r.justificativaCredito}` : ''}
                  </p>
                  <div className="rolagem-tabela-previa">
                    <table className="tabela tabela-compacta">
                      <thead>
                        <tr>
                          <th>Parcela</th>
                          <th className="numero">Anterior</th>
                          <th className="numero">Novo</th>
                          <th className="numero">Já pago</th>
                          <th className="numero">Crédito</th>
                          <th>Situação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.parcelas.map((x) => (
                          <tr key={x.numero}>
                            <td>
                              {x.parcela} · {x.numero}
                            </td>
                            <td className="numero">{rs(x.anterior)}</td>
                            <td className="numero">{rs(x.novo)}</td>
                            <td className="numero">{rs(x.pago)}</td>
                            <td className="numero">{Number(x.credito) > 0 ? rs(x.credito) : '—'}</td>
                            <td>{x.cancelada ? 'cancelada (histórico preservado)' : Number(x.novo) === 0 ? 'zerada pelo documento (pagamento preservado)' : x.anterior === x.novo ? 'sem mudança' : 'valor alterado'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </section>
          )}
          {retirados.length > 0 && (
            <section className="cartao" data-teste="anexos-retirados">
              <h2>Arquivos retirados</h2>
              <ul className="lista-arquivos">
                {retirados.map((x) => (
                  <li key={x.id}>
                    <span className="nome-retirado">{x.nome_original}</span> ({x.papel === 'documento' ? 'documento da despesa' : 'comprovante'}) · enviado em {dataBr(x.criado_em)}
                    {x.enviado_por ? ` por ${x.enviado_por}` : ''} · retirado em {dataBr(x.retirado_em)}
                    {x.retirado_por ? ` por ${x.retirado_por}` : ''} — {x.motivo_retirada}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
        {f.recorrencia && (
          <QuadroRecorrencia
            r={f.recorrencia}
            podeAlterar={pode('editar', 'conta_pagar')}
            podeEncerrar={pode('cancelar', 'conta_pagar')}
            aoAlterar={() => setModal('alterar-serie')}
            aoEncerrar={() => setModal('encerrar-serie')}
          />
        )}
      </div>
      {modal === 'pagar' && <RegistrarPagamento obrigacoes={[o]} aoFechar={() => setModal('')} aoSalvar={recarregar} />}
      {modal === 'documento' && <CompletarDocumento ficha={f} aoFechar={() => setModal('')} aoSalvar={recarregar} />}
      {modal === 'cancelar' && <CancelarDespesa ficha={f} aoFechar={() => setModal('')} aoSalvar={recarregar} />}
      {modal === 'editar' && <EditarDespesa ficha={f} aoFechar={() => setModal('')} aoSalvar={recarregar} />}
      {modal === 'reprogramar' && <ReprogramarVencimento obrigacao={o.numero} vencimento={o.vencimento} descricao={d.descricao} aoFechar={() => setModal('')} aoConcluir={recarregar} />}
      {estornar && <EstornarPagamento pagamento={estornar} obrigacao={o} periodos={periodosCarga.dados ?? []} aoFechar={() => setEstornar(null)} aoSalvar={recarregar} />}
      {anexarEm && <AnexarComprovante pagamento={anexarEm} aoFechar={() => setAnexarEm(null)} aoSalvar={recarregar} />}
      {retirar && <RetirarAnexo anexo={retirar} aoFechar={() => setRetirar(null)} aoSalvar={recarregar} />}
      {recuperar && <RegistrarRecuperacao {...recuperar} aoFechar={() => setRecuperar(null)} aoSalvar={recarregar} />}
      {estornarRcp && <EstornarRecuperacao numero={estornarRcp} aoFechar={() => setEstornarRcp(null)} aoSalvar={recarregar} />}
      {modal === 'alterar-serie' && f.recorrencia && <AlterarRecorrencia r={f.recorrencia} esta={o} aoFechar={() => setModal('')} aoSalvar={recarregar} />}
      {modal === 'encerrar-serie' && f.recorrencia && <EncerrarRecorrencia r={f.recorrencia} esta={o} aoFechar={() => setModal('')} aoSalvar={recarregar} />}
    </>
  );
}

/* ---------- Recorrência (F05 lateral, F15 alterar, encerrar) ---------- */
const ACAO_EVENTO: Record<string, string> = { alterar_esta: 'Alterou só a ocorrência', alterar_futuras: 'Alterou esta e as futuras', encerrar: 'Encerrou a série' };
function QuadroRecorrencia({ r, podeAlterar, podeEncerrar, aoAlterar, aoEncerrar }: { r: Recorrencia; podeAlterar: boolean; podeEncerrar: boolean; aoAlterar: () => void; aoEncerrar: () => void }) {
  return (
    <aside className="cartao quadro-recorrencia" data-teste="quadro-recorrencia">
      <h2>Recorrência</h2>
      <dl className="lista-recorrencia">
        <div>
          <dt>Série</dt>
          <dd>
            {r.numero} · {r.frequencia}
          </dd>
        </div>
        <div>
          <dt>Valor previsto</dt>
          <dd>{rs(r.valorPrevisto)}</dd>
        </div>
        <div>
          <dt>Esta ocorrência</dt>
          <dd>{ordinal(r.estaOcorrencia)}</dd>
        </div>
        <div>
          <dt>Vencimento</dt>
          <dd>todo dia {r.diaVencimento} (ou o último dia do mês)</dd>
        </div>
        {r.proxima && (
          <div>
            <dt>Próxima</dt>
            <dd>
              {mesBr(r.proxima.competencia.slice(0, 7))} · vence {dataBr(r.proxima.vencimento)} · estimada
            </dd>
          </div>
        )}
        <div>
          <dt>Projeção</dt>
          <dd>{r.encerrada ? 'interrompida' : r.termino ? `${r.termino.ocorrencias} ocorrências (término definido)` : `móvel · 12 meses${r.projetadaAte ? ` (até ${mesBr(r.projetadaAte)})` : ''}`}</dd>
        </div>
        <div>
          <dt>Término</dt>
          <dd>{r.encerrada ? `encerrada a partir de ${mesBr(r.encerrada.aPartir)}` : r.termino ? `após ${r.termino.ocorrencias} ocorrências` : 'sem término'}</dd>
        </div>
      </dl>
      {r.encerrada && <p className="texto-secundario sem-margem">Motivo: {r.encerrada.motivo}</p>}
      {!r.encerrada && (podeAlterar || podeEncerrar) && (
        <div className="acoes-recorrencia">
          {podeAlterar && (
            <button type="button" className="botao-secundario" onClick={aoAlterar} data-teste="alterar-recorrencia">
              Alterar recorrência
            </button>
          )}
          {podeEncerrar && (
            <button type="button" className="botao-link" onClick={aoEncerrar} data-teste="encerrar-recorrencia">
              Encerrar a partir de…
            </button>
          )}
        </div>
      )}
      {r.eventos.length > 0 && (
        <div className="historico-recorrencia" data-teste="historico-recorrencia">
          <strong>Histórico da série</strong>
          <ul className="lista-arquivos">
            {r.eventos.map((e, i) => (
              <li key={i}>
                {ACAO_EVENTO[e.acao] ?? e.acao} ({mesBr(e.a_partir)}) · {dataBr(e.data_hora)}
                {e.autor ? ` por ${e.autor}` : ''} — {e.motivo}
                <small className="bloco texto-secundario">
                  {e.afetadas.length} {e.acao === 'encerrar' ? 'cancelada(s)' : 'alterada(s)'} · {e.preservadas.length} preservada(s)
                </small>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

interface LinhaPrevia {
  numero: string;
  competencia: string;
  estado?: string;
  pagamento?: string;
  acao: 'altera' | 'preservada' | 'cancela';
  motivo: string | null;
  valor: { de: string; para: string } | string;
  vencimento: { de: string; para: string } | string;
}
function TabelaPrevia({ linhas, encerrar }: { linhas: LinhaPrevia[]; encerrar?: boolean }) {
  return (
    <div className="rolagem">
      <table className="tabela tabela-compacta tabela-previa" data-teste="tabela-previa">
        <thead>
          <tr>
            <th>Ocorrência</th>
            <th>Situação</th>
            <th>O que acontece</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.numero} className={l.acao === 'preservada' ? 'linha-preservada' : undefined}>
              <td className="sem-quebra">
                {mesBr(l.competencia)}
                <small className="bloco texto-secundario">{l.numero}</small>
              </td>
              <td>{l.acao === 'preservada' ? <span className="texto-secundario">{l.motivo}</span> : <span className="selo-projetado">Projetado</span>}</td>
              <td>
                {l.acao === 'preservada' ? (
                  'Fica como está'
                ) : encerrar ? (
                  <strong>Cancelada (histórico preservado)</strong>
                ) : typeof l.valor === 'object' && typeof l.vencimento === 'object' ? (
                  <>
                    {l.valor.de !== l.valor.para ? (
                      <strong>
                        {rs(l.valor.de)} → {rs(l.valor.para)}
                      </strong>
                    ) : (
                      <span>{rs(l.valor.de)}</span>
                    )}
                    {l.vencimento.de !== l.vencimento.para && (
                      <small className="bloco">
                        vence {dataBr(l.vencimento.de)} → {dataBr(l.vencimento.para)}
                      </small>
                    )}
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AlterarRecorrencia({ r, esta, aoFechar, aoSalvar }: { r: Recorrencia; esta: Obrigacao; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const a = apoio.dados;
  const [escopo, setEscopo] = useState<'esta' | 'futuras'>('futuras');
  const [aPartir, setAPartir] = useState(esta.competencia);
  const [valor, setValor] = useState('');
  const [dia, setDia] = useState('');
  const [contaId, setContaId] = useState('');
  const [centroId, setCentroId] = useState<string | null>(null);
  const [descricao, setDescricao] = useState('');
  const [motivo, setMotivo] = useState('');
  const [previa, setPrevia] = useState<{ linhas: LinhaPrevia[] } | null>(null);
  const [erro, setErro] = useState('');
  const corpo = {
    escopo,
    obrigacao: esta.numero,
    a_partir: aPartir,
    ...(valor ? { valor } : {}),
    ...(dia ? { dia_vencimento: Number(dia) } : {}),
    ...(contaId ? { plano_contas_id: contaId } : {}),
    ...(centroId !== null ? { centro_custo_id: centroId || null } : {}),
    ...(descricao ? { descricao } : {}),
  };
  const chave = JSON.stringify(corpo);
  useEffect(() => {
    if (!valor && !dia && !contaId && centroId === null && !descricao) return setPrevia(null);
    const t = setTimeout(() => {
      api
        .post<{ linhas: LinhaPrevia[] }>(`/api/contas-pagar/recorrencias/${r.id}/previa-alteracao`, JSON.parse(chave))
        .then((p) => (setPrevia(p), setErro('')))
        .catch((e) => (setPrevia(null), setErro((e as Error).message)));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);
  const futuras = r.ocorrencias.filter((x) => x.competencia >= esta.competencia && !x.cancelada);
  const nAltera = previa?.linhas.filter((l) => l.acao === 'altera').length ?? 0;
  const aplicar = async () => {
    setErro('');
    try {
      await api.post(`/api/contas-pagar/recorrencias/${r.id}/alterar`, { ...corpo, motivo });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo="Alterar recorrência"
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!previa || nAltera === 0 || motivo.trim().length < 4} onClick={() => void aplicar()} data-teste="aplicar-alteracao">
            {nAltera ? `Aplicar a ${nAltera} ${nAltera === 1 ? 'ocorrência' : 'ocorrências'}` : 'Aplicar'}
          </button>
        </>
      }
    >
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="form-documento" data-teste="form-alterar-serie">
          <p className="faixa-resumo-modal">
            <span>
              Série <strong>{r.numero}</strong>
            </span>
            <span>{r.descricao}</span>
            <span>
              {r.frequencia} · {rs(r.valorPrevisto)} · dia {r.diaVencimento}
            </span>
          </p>
          <fieldset className="opcoes-cartao lista">
            <legend>O que muda</legend>
            <label className={`opcao-cartao${escopo === 'esta' ? ' escolhida' : ''}`}>
              <input type="radio" checked={escopo === 'esta'} onChange={() => setEscopo('esta')} data-teste="escopo-esta" />
              <span>
                <strong>Somente esta ocorrência</strong>
                <small>
                  {mesBr(esta.competencia)} · {esta.numero}
                </small>
              </span>
            </label>
            <label className={`opcao-cartao${escopo === 'futuras' ? ' escolhida' : ''}`}>
              <input type="radio" checked={escopo === 'futuras'} onChange={() => setEscopo('futuras')} data-teste="escopo-futuras" />
              <span>
                <strong>Esta e as futuras</strong>
                <small>a série passa a usar os novos valores nas próximas projeções</small>
              </span>
            </label>
          </fieldset>
          <div className="grade-form duas">
            {escopo === 'futuras' && (
              <Campo rotulo="A partir de">
                {(id) => (
                  <select id={id} value={aPartir} onChange={(e) => setAPartir(e.target.value)} data-teste="a-partir">
                    {futuras.map((x) => (
                      <option key={x.numero} value={x.competencia}>
                        {mesBr(x.competencia)}
                        {x.numero === esta.numero ? ' (esta ocorrência)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </Campo>
            )}
            <Campo rotulo="Novo valor previsto">{(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" placeholder={rs(r.valorPrevisto)} data-teste="serie-valor" />}</Campo>
            <Campo rotulo="Novo dia de vencimento">{(id) => <input id={id} value={dia} onChange={(e) => setDia(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" placeholder={String(r.diaVencimento)} data-teste="serie-dia" />}</Campo>
            <Campo rotulo="Conta gerencial">
              {(id) => (
                <select id={id} value={contaId} onChange={(e) => setContaId(e.target.value)} data-teste="serie-conta">
                  <option value="">Sem mudança</option>
                  {a.contasDespesa.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} — {c.nome}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            <Campo rotulo="Centro de custo">
              {(id) => (
                <select id={id} value={centroId ?? '__'} onChange={(e) => setCentroId(e.target.value === '__' ? null : e.target.value)} data-teste="serie-centro">
                  <option value="__">Sem mudança</option>
                  <option value="">Sem centro de custo</option>
                  {a.centros.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            <div className="largura-total">
              <Campo rotulo="Nova descrição">{(id) => <input id={id} value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={200} placeholder={r.descricao} data-teste="serie-descricao" />}</Campo>
            </div>
          </div>
          <p className="texto-secundario sem-margem">Favorecido e frequência não mudam numa série: para isso, encerre esta série e crie outra.</p>
          {previa && (
            <section className="quadro-depois" data-teste="previa-alteracao">
              <h3>Prévia: {nAltera} mudam · {previa.linhas.length - nAltera} ficam como estão</h3>
              <TabelaPrevia linhas={previa.linhas} />
              <small className="texto-secundario">Confirmadas, pagas (inclusive parcialmente), com documento ou anexo nunca são alteradas.</small>
            </section>
          )}
          <Campo rotulo="Motivo">{(id) => <textarea id={id} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} data-teste="motivo-alteracao" />}</Campo>
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-alteracao">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function EncerrarRecorrencia({ r, esta, aoFechar, aoSalvar }: { r: Recorrencia; esta: Obrigacao; aoFechar: () => void; aoSalvar: () => void }) {
  const opcoes = r.ocorrencias.filter((x) => !x.cancelada);
  const [aPartir, setAPartir] = useState(esta.competencia);
  const [motivo, setMotivo] = useState('');
  const [previa, setPrevia] = useState<{ linhas: LinhaPrevia[] } | null>(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    api
      .post<{ linhas: LinhaPrevia[] }>(`/api/contas-pagar/recorrencias/${r.id}/previa-encerramento`, { a_partir: aPartir })
      .then((p) => (setPrevia(p), setErro('')))
      .catch((e) => (setPrevia(null), setErro((e as Error).message)));
  }, [r.id, aPartir]);
  const n = previa?.linhas.filter((l) => l.acao === 'cancela').length ?? 0;
  const encerrar = async () => {
    setErro('');
    try {
      await api.post(`/api/contas-pagar/recorrencias/${r.id}/encerrar`, { a_partir: aPartir, motivo });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Encerrar a recorrência ${r.numero}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!previa || motivo.trim().length < 4} onClick={() => void encerrar()} data-teste="confirmar-encerramento">
            Encerrar a partir de {mesBr(aPartir)}
          </button>
        </>
      }
    >
      <div className="form-documento" data-teste="form-encerrar-serie">
        <p className="faixa-resumo-modal">
          <span>
            Série <strong>{r.numero}</strong>
          </span>
          <span>{r.descricao}</span>
          <span>
            {r.frequencia} · {rs(r.valorPrevisto)}
          </span>
        </p>
        <div className="grade-form duas">
          <Campo rotulo="Encerrar a partir de">
            {(id) => (
              <select id={id} value={aPartir} onChange={(e) => setAPartir(e.target.value)} data-teste="encerrar-a-partir">
                {opcoes.map((x) => (
                  <option key={x.numero} value={x.competencia}>
                    {mesBr(x.competencia)}
                    {x.numero === esta.numero ? ' (esta ocorrência)' : ''}
                  </option>
                ))}
              </select>
            )}
          </Campo>
        </div>
        {previa && (
          <section className="quadro-depois" data-teste="previa-encerramento">
            <h3>
              Prévia: {n} {n === 1 ? 'ocorrência estimada será cancelada' : 'ocorrências estimadas serão canceladas'} · {previa.linhas.length - n} seguem como obrigação
            </h3>
            <TabelaPrevia linhas={previa.linhas} encerrar />
            <small className="texto-secundario">Nada é apagado: as canceladas ficam no histórico. Confirmadas e as que têm pagamento continuam valendo. Depois disso, a série não projeta mais.</small>
          </section>
        )}
        <Campo rotulo="Motivo do encerramento">{(id) => <textarea id={id} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} data-teste="motivo-encerramento" />}</Campo>
        {erro && (
          <p className="aviso aviso-erro" role="alert">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}

/* ---------- Completar documento (F07/F18) ---------- */
/** Conferência do valor vigente da despesa depois da confirmação por parcela (sem somar o valor de antes). */
function ResumoVigente({ parcelas }: { parcelas: Obrigacao[] }) {
  const c = (v: string) => Math.round(Number(v) * 100);
  const vigentes = parcelas.map((p) => c(p.valor));
  const aplicado = parcelas.reduce((t, p) => t + c(p.pago), 0);
  const credito = parcelas.reduce((t, p) => t + c(p.creditoDocumento), 0);
  const saldo = parcelas.reduce((t, p) => t + c(p.saldo), 0);
  return (
    <p className="faixa-resumo-modal resumo-vigente" data-teste="resumo-vigente">
      <span>
        Valores vigentes {vigentes.map((v) => rs(v / 100)).join(' + ')} = <strong>{rs(vigentes.reduce((t, v) => t + v, 0) / 100)}</strong>
      </span>
      <span>
        Pagamentos <strong>{rs((aplicado + credito) / 100)}</strong> (preservados)
      </span>
      <span>
        Aplicado à despesa <strong>{rs(aplicado / 100)}</strong>
      </span>
      <span>
        Crédito a recuperar <strong>{rs(credito / 100)}</strong>
      </span>
      <span>
        Saldo a pagar <strong>{rs(saldo / 100)}</strong>
      </span>
    </p>
  );
}

/* ---------- parcelada: valor efetivo do documento por parcela (prévia editável; critérios só como auxílio) ---------- */
interface PreviaParcelasApi {
  versao: string;
  valorAnterior: string;
  valorDocumento: string;
  criterio: string | null;
  parcelas: {
    obrigacaoId: string;
    numero: string;
    parcela: number;
    vencimento: string;
    anterior: string;
    pago: string;
    novo: string;
    aplicado: string;
    saldo: string;
    creditoAnterior: string;
    credito: string;
    descontoAnterior: string;
    desconto: string;
    cancelada: boolean;
    situacao: 'pago' | 'parcial' | 'aberto' | 'credito' | 'zerada' | 'cancelada';
  }[];
  totais: { anterior: string; pago: string; novo: string; aplicado: string; credito: string; creditoAnterior: string; desconto: string; descontoAnterior: string; saldo: string };
  confere: boolean;
  aumentaCredito: boolean;
  erros: string[];
}
export interface EstadoPreviaParcelas {
  versao: string;
  parcelas: { obrigacao_id: string; valor_novo: string }[];
  confere: boolean;
  credito: number;
  aumentaCredito: boolean;
  somaDiferente: boolean;
}
const AUXILIOS: { id: string; nome: string }[] = [
  { id: 'proporcional', nome: 'Proporcional ao saldo' },
  { id: 'ultima', nome: 'Só na última em aberto' },
  { id: 'proxima', nome: 'Só na próxima em aberto' },
  { id: 'igual', nome: 'Igual entre as em aberto' },
  { id: 'atuais', nome: 'Voltar aos valores atuais' },
];
function PreviaParcelas({ despesaId, valorDoc, total, recarga, aoMudar }: { despesaId: string; valorDoc: string; total: number; recarga: number; aoMudar: (e: EstadoPreviaParcelas | null) => void }) {
  // uma parcela só (avulsa ou ocorrência): o valor novo é o próprio documento; com várias, o usuário distribui
  const [pedido, setPedido] = useState<{ criterio?: string; manual?: Record<string, string> }>({ criterio: total === 1 ? 'ultima' : 'atuais' });
  const [valores, setValores] = useState<Record<string, string>>({});
  const [r, setR] = useState<PreviaParcelasApi | null>(null);
  const [erro, setErro] = useState('');
  useEffect(() => {
    if (!(centavosDe(valorDoc) > 0)) return;
    let vivo = true;
    const t = setTimeout(
      () =>
        void api
          .post<PreviaParcelasApi>(`/api/contas-pagar/despesas/${despesaId}/previa-parcelas`, {
            valor_documento: valorDoc,
            ...(pedido.manual ? { parcelas: Object.entries(pedido.manual).map(([obrigacao_id, valor_novo]) => ({ obrigacao_id, valor_novo: valor_novo.trim() || '0' })) } : { criterio: pedido.criterio }),
          })
          .then((x) => {
            if (!vivo) return;
            setErro('');
            setR(x);
            if (!pedido.manual) setValores(Object.fromEntries(x.parcelas.map((l) => [l.obrigacaoId, deCentavos(Math.round(Number(l.novo) * 100))])));
          })
          .catch((e) => vivo && (setErro((e as Error).message), setR(null))),
      pedido.manual ? 350 : 0,
    );
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [despesaId, valorDoc, pedido, recarga]);
  useEffect(() => {
    aoMudar(r ? { versao: r.versao, parcelas: r.parcelas.map((l) => ({ obrigacao_id: l.obrigacaoId, valor_novo: l.novo })), confere: r.confere, credito: Math.round(Number(r.totais.credito) * 100), aumentaCredito: r.aumentaCredito, somaDiferente: r.totais.novo !== r.valorDocumento } : null);
  }, [r, aoMudar]);
  const editar = (id: string, v: string) => {
    const novos = { ...valores, [id]: v };
    setValores(novos);
    setPedido({ manual: novos });
  };
  const SIT: Record<PreviaParcelasApi['parcelas'][number]['situacao'], ReactNode> = {
    pago: <SeloSituacao texto="Pago" tom="positivo" />,
    parcial: <SeloSituacao texto="Parcial" tom="parcial" />,
    aberto: <SeloSituacao texto="Em aberto" tom="aberto" />,
    credito: null,
    zerada: null,
    cancelada: <span className="texto-secundario">cancelada (histórico preservado)</span>,
  };
  // crédito e desconto antes → depois (recalculados a partir dos fatos; o que sobra é revertido, nada é apagado)
  const ajusteTexto = (l: PreviaParcelasApi['parcelas'][number]) => {
    const partes: string[] = [];
    if (l.credito !== l.creditoAnterior) partes.push(Number(l.creditoAnterior) > 0 ? `crédito ${rs(l.creditoAnterior)} → ${rs(l.credito)}` : `crédito a recuperar ${rs(l.credito)}`);
    else if (Number(l.credito) > 0) partes.push(`crédito a recuperar ${rs(l.credito)}`);
    if (l.desconto !== l.descontoAnterior) partes.push(`desconto ${rs(l.descontoAnterior)} → ${rs(l.desconto)}`);
    else if (Number(l.desconto) > 0) partes.push(`desconto ${rs(l.desconto)}`);
    return partes.join(' · ');
  };
  return (
    <section className="quadro-form" data-teste="previa-parcelas">
      <h3>
        Valor efetivo nas parcelas{' '}
        <Ajuda titulo="Valor efetivo nas parcelas">
          Sem rateio obrigatório: preencha cada parcela conforme a cobrança efetiva do fornecedor. Os botões só ajudam a preencher. Competência, vencimentos e pagamentos não mudam; cada valor anterior → novo fica na auditoria com a justificativa. O valor novo pode ser zero: sem pagamento, a parcela fica cancelada (histórico); com pagamento, o pagamento é preservado e todo o pago vira crédito a recuperar. O que já foi pago acima do valor novo vira crédito (parte do pagamento já feito, não uma saída nova). Crédito ou desconto de confirmação anterior é recalculado: o que sobra é revertido com registro vinculado, sem estorno e sem apagar nada.
        </Ajuda>
      </h3>
      {total > 1 && (
        <p className="texto-secundario sem-margem auxilios-previa">
          Distribuir como auxílio (você confere e pode editar cada parcela):{' '}
          {AUXILIOS.map((a, i) => (
            <span key={a.id}>
              {i > 0 && ' · '}
              <button type="button" className="botao-link" onClick={() => setPedido({ criterio: a.id })} data-teste={`auxilio-${a.id}`}>
                {a.nome}
              </button>
            </span>
          ))}
        </p>
      )}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {r && (
        <>
          <div className="rolagem-tabela-previa">
            <table className="tabela tabela-compacta tabela-previa-parcelas" data-teste="tabela-previa-parcelas">
              <thead>
                <tr>
                  <th>Parcela</th>
                  <th>Vencimento (mantido)</th>
                  <th className="numero">Valor anterior</th>
                  <th className="numero">Já pago (preservado)</th>
                  <th className="numero">Valor novo</th>
                  <th className="numero">Saldo resultante</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {r.parcelas.map((l) => (
                  <tr key={l.obrigacaoId}>
                    <td>
                      {l.parcela}/{total}
                    </td>
                    <td>{dataBr(l.vencimento)}</td>
                    <td className="numero">{rs(l.anterior)}</td>
                    <td className="numero">{rs(l.pago)}</td>
                    <td className="numero">
                      <input className="valor-parcela-previa" value={valores[l.obrigacaoId] ?? ''} onChange={(e) => editar(l.obrigacaoId, e.target.value)} inputMode="decimal" aria-label={`Valor novo da parcela ${l.parcela}`} data-teste={`novo-parcela-${l.parcela}`} />
                    </td>
                    <td className="numero">{rs(l.saldo)}</td>
                    <td>
                      {l.situacao === 'zerada' ? <SeloSituacao texto="Zerada pelo documento" tom="encerrado" /> : l.situacao === 'credito' ? null : SIT[l.situacao]}
                      {ajusteTexto(l) && <div className="texto-credito">{ajusteTexto(l)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="linha-total">
                  <td colSpan={2}>
                    <strong>Total</strong>
                  </td>
                  <td className="numero">
                    <strong>{rs(r.totais.anterior)}</strong>
                  </td>
                  <td className="numero">
                    <strong>{rs(r.totais.pago)}</strong>
                  </td>
                  <td className="numero" data-teste="total-previa">
                    <strong>{rs(r.totais.novo)}</strong>{' '}
                    {r.totais.novo === r.valorDocumento ? <span className="texto-confere">✓ = documento</span> : <span className="texto-alerta">≠ documento ({rs(r.valorDocumento)})</span>}
                  </td>
                  <td className="numero">
                    <strong>{rs(r.totais.saldo)}</strong>
                  </td>
                  <td>{Number(r.totais.credito) > 0 && <strong className="texto-credito">crédito {rs(r.totais.credito)}</strong>}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="faixa-resumo-modal" data-teste="resumo-previa-parcelas">
            <span>
              Documento <strong>{rs(r.valorDocumento)}</strong>
            </span>
            <span>
              Pagamentos <strong>{rs(r.totais.pago)}</strong> (não mudam)
            </span>
            <span>
              Aplicado às parcelas <strong>{rs(r.totais.aplicado)}</strong>
            </span>
            <span>
              Crédito a recuperar <strong>{rs(r.totais.credito)}</strong>
              {r.totais.credito !== r.totais.creditoAnterior && Number(r.totais.creditoAnterior) > 0 ? ` (antes ${rs(r.totais.creditoAnterior)})` : ''}
            </span>
            {(Number(r.totais.desconto) > 0 || Number(r.totais.descontoAnterior) > 0) && (
              <span>
                Desconto <strong>{rs(r.totais.desconto)}</strong>
                {r.totais.desconto !== r.totais.descontoAnterior ? ` (antes ${rs(r.totais.descontoAnterior)})` : ''}
              </span>
            )}
            <span>
              Saldo a pagar <strong>{rs(r.totais.saldo)}</strong>
            </span>
          </p>
          {r.erros.length > 0 && (
            <ul className="erros-previa texto-alerta" data-teste="erros-previa">
              {r.erros.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function CompletarDocumento({ ficha: f, aoFechar, aoSalvar }: { ficha: Ficha; aoFechar: () => void; aoSalvar: () => void }) {
  const { pode } = useSessao();
  const d = f.despesa;
  const o = f.obrigacao;
  const [tipo, setTipo] = useState(o.documento.tipo ?? '');
  const [numero, setNumero] = useState(o.documento.numero ?? '');
  const [emissao, setEmissao] = useState(o.documento.emissao ?? '');
  const [confirmar, setConfirmar] = useState(d.estadoDocumento === 'confirmado');
  const [valorDoc, setValorDoc] = useState(deCentavos(Math.round(Number(d.valorDocumento ?? d.valorEstimado) * 100)));
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [justValor, setJustValor] = useState('');
  const [justCredito, setJustCredito] = useState('');
  const [erro, setErro] = useState('');
  const refErro = useRef<HTMLParagraphElement>(null);
  // a mensagem (ex.: prévia desatualizada) aparece na vista, mesmo com o corpo do modal rolado
  useEffect(() => {
    if (erro) refErro.current?.scrollIntoView({ block: 'nearest' });
  }, [erro]);
  const temPagamento = f.pagamentos.some((p) => !p.estorno);
  // valor efetivo do documento diferente do lançado, depois de pagamento: caminho explícito (justificativa; saldo recalculado)
  const novo = centavosDe(valorDoc);
  const atual = Math.round(Number(o.valor) * 100);
  const pago = Math.round(Number(o.pago) * 100);
  const mudaValor = confirmar && novo > 0 && novo !== atual;
  const valorAposPagamento = mudaValor && temPagamento;
  const abaixoDoPago = valorAposPagamento && novo < pago ? pago - novo : 0;
  // parcelada: o documento vale para a despesa inteira; a diferença vai para as parcelas pela prévia editável
  // parcelada — ou avulsa/recorrente com desconto ou crédito anterior — vai pela prévia (crédito/desconto recalculados)
  const comAjuste = f.parcelas.some((x) => !x.cancelada && (Number(x.desconto) > 0 || Number(x.creditoDocumento) > 0));
  const parcelada = d.tipo === 'parcelada' || comAjuste;
  const totalParcelas = f.parcelas.filter((x) => !x.cancelada).reduce((s, x) => s + Math.round(Number(x.valor) * 100), 0);
  const mudaParcelas = parcelada && confirmar && novo > 0 && novo !== totalParcelas;
  const [previa, setPrevia] = useState<EstadoPreviaParcelas | null>(null);
  const [recarga, setRecarga] = useState(0);
  const chave = useMemo(() => novaChave(), []);
  const salvar = async () => {
    setErro('');
    try {
      await api.post(`/api/contas-pagar/despesas/${d.id}/documento`, {
        tipo: tipo || null,
        numero: numero || null,
        emissao: emissao || null,
        // só confirma quando muda algo (num mês fechado, valor e estado não mudam; nº e emissão sim)
        ...(confirmar && (d.estadoDocumento !== 'confirmado' || novo !== Math.round(Number(d.valorDocumento) * 100) || novo !== (parcelada ? totalParcelas : atual)) ? { confirmar: true, valor_documento: valorDoc } : {}),
        ...(mudaParcelas && previa
          ? {
              versao: previa.versao,
              parcelas: previa.parcelas,
              chave_idempotencia: chave,
              justificativa_valor: justValor,
              ...(previa.aumentaCredito ? { diferenca: { tipo: 'credito_recuperar', justificativa: justCredito } } : {}),
            }
          : {}),
        ...(!parcelada && valorAposPagamento ? { justificativa_valor: justValor } : {}),
        ...(!parcelada && abaixoDoPago > 0 ? { diferenca: { tipo: 'credito_recuperar', justificativa: justCredito } } : {}),
        arquivo: await arquivoParaApi(arquivo),
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
      // prévia desatualizada (ex.: pagamento registrado agora): recarrega a prévia para conferir de novo
      if (e instanceof ErroApi && e.codigo === 'previa_desatualizada') setRecarga((x) => x + 1);
    }
  };
  const bloqueioParcelas = mudaParcelas && (!previa || !previa.confere || justValor.trim().length < 4 || (previa.aumentaCredito && justCredito.trim().length < 4));
  return (
    <Modal
      titulo={mudaParcelas ? 'Completar documento — valor efetivo nas parcelas' : `Completar documento — ${d.numero}`}
      aoFechar={aoFechar}
      medio={!mudaParcelas}
      largo={mudaParcelas}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={parcelada ? bloqueioParcelas : (valorAposPagamento && justValor.trim().length < 4) || (abaixoDoPago > 0 && justCredito.trim().length < 4)}
            onClick={() => void salvar()}
            data-teste="salvar-documento"
          >
            {mudaParcelas ? (previa && !previa.confere ? (previa.somaDiferente ? 'Confira: a soma das parcelas precisa ser o valor do documento' : 'Confira os avisos da prévia') : 'Confirmar valor efetivo nas parcelas') : valorAposPagamento ? 'Confirmar valor efetivo' : 'Salvar documento'}
          </button>
        </>
      }
    >
      <div className="form-documento" data-teste="form-documento">
        {temPagamento && (
          <p className="aviso aviso-info">A despesa já tem pagamento: nº, emissão e arquivo são completados nela mesma (nenhuma despesa nova). Valor diferente do lançado não muda em silêncio.</p>
        )}
        {d.periodoFechado && <p className="aviso aviso-alerta">Competência {mesExtenso(d.competencia)} fechada: só tipo, nº e emissão do documento podem ser completados.</p>}
        <div className="grade-form tres">
          <Campo rotulo="Tipo">
            {(id) => (
              <select id={id} value={tipo} onChange={(e) => setTipo(e.target.value)} data-teste="doc-tipo">
                <option value="">Não informado</option>
                {Object.entries(TIPOS_DOCUMENTO).map(([k, n]) => (
                  <option key={k} value={k}>
                    {n}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          <Campo rotulo="Nº do documento">{(id) => <input id={id} value={numero} onChange={(e) => setNumero(e.target.value)} maxLength={60} data-teste="doc-numero" />}</Campo>
          <CampoData rotulo="Emissão" valor={emissao} aoMudar={setEmissao} teste="doc-emissao" />
        </div>
        <label className="caixa-marcar">
          <input type="checkbox" checked={confirmar} onChange={(e) => setConfirmar(e.target.checked)} data-teste="doc-confirmar" />
          <span>
            Confirmar o valor pelo documento
            <small className="bloco texto-secundario">o valor estimado ({rs(d.valorEstimado)}) continua guardado ao lado</small>
          </span>
        </label>
        {confirmar && <Campo rotulo="Valor do documento">{(id) => <input id={id} value={valorDoc} onChange={(e) => setValorDoc(e.target.value)} inputMode="decimal" data-teste="doc-valor" />}</Campo>}
        {mudaParcelas && (
          <>
            <p className="faixa-resumo-modal">
              <span>
                Despesa <strong>{d.numero}</strong> · {d.descricao}
                {d.tipo === 'parcelada' ? ` · parcelada em ${o.parcelas}` : ''}
              </span>
              <span>
                Competência <strong>{mesBr(d.competencia)}</strong> (mantida)
              </span>
              <span>
                Estimado <strong>{rs(d.valorEstimado)}</strong> (guardado)
              </span>
              <span>
                Documento <strong>{rs(novo / 100)}</strong>
              </span>
            </p>
            {erro && (
              <p className="aviso aviso-erro" role="alert" data-teste="erro-documento" ref={refErro}>
                {erro}
              </p>
            )}
            <PreviaParcelas despesaId={d.id} valorDoc={valorDoc} total={o.parcelas} recarga={recarga} aoMudar={setPrevia} />
            <div className="grade-form duas">
              <Campo rotulo="Justificativa da mudança de valor">{(id) => <input id={id} value={justValor} onChange={(e) => setJustValor(e.target.value)} maxLength={500} data-teste="doc-justificativa-valor" />}</Campo>
              {previa && previa.aumentaCredito && (
                <Campo rotulo="Justificativa do crédito a recuperar">
                  {(id) => <input id={id} value={justCredito} onChange={(e) => setJustCredito(e.target.value)} maxLength={500} data-teste="doc-justificativa-credito" />}
                </Campo>
              )}
            </div>
          </>
        )}
        {valorAposPagamento && !parcelada && (
          <section className="quadro-form" data-teste="valor-apos-pagamento">
            <h3>
              Valor efetivo depois do pagamento{' '}
              <Ajuda titulo="Valor efetivo">O valor estimado continua guardado e os pagamentos não mudam. A obrigação passa a valer o documento e o saldo é recalculado; a mudança fica na auditoria com a justificativa.</Ajuda>
            </h3>
            <p className="faixa-resumo-modal">
              <span>
                Lançado <strong>{rs(atual / 100)}</strong>
              </span>
              <span>
                Documento <strong>{rs(novo / 100)}</strong>
              </span>
              <span>
                Já pago <strong>{rs(pago / 100)}</strong>
              </span>
              <span>
                Saldo depois <strong>{rs(Math.max(novo - pago - Math.round(Number(o.desconto) * 100), 0) / 100)}</strong>
              </span>
            </p>
            <Campo rotulo="Justificativa da mudança de valor">{(id) => <input id={id} value={justValor} onChange={(e) => setJustValor(e.target.value)} maxLength={500} data-teste="doc-justificativa-valor" />}</Campo>
            {abaixoDoPago > 0 && (
              <>
                <div className="aviso aviso-atencao-fin" role="status" data-teste="doc-abaixo-do-pago">
                  <strong>◐ Atenção:</strong> o documento fica {rs(abaixoDoPago / 100)} abaixo do que já foi pago.
                  <ul>
                    <li>A diferença vira crédito a recuperar com o favorecido (conta CRD-001). O pagamento não muda.</li>
                  </ul>
                </div>
                <Campo rotulo="Justificativa do crédito a recuperar">
                  {(id) => <input id={id} value={justCredito} onChange={(e) => setJustCredito(e.target.value)} maxLength={500} data-teste="doc-justificativa-credito" />}
                </Campo>
              </>
            )}
          </section>
        )}
        {pode('enviar', 'documento_financeiro') && <Arquivo rotulo="Arquivo do documento" arquivo={arquivo} setArquivo={setArquivo} teste="doc-arquivo" />}
        {erro && !mudaParcelas && (
          <p className="aviso aviso-erro" role="alert" data-teste="erro-documento" ref={refErro}>
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}

/* ---------- Estorno (F08 / F19) ---------- */
function EstornarPagamento({
  pagamento: p,
  obrigacao: o,
  periodos,
  aoFechar,
  aoSalvar,
}: {
  pagamento: Pagamento;
  obrigacao: Obrigacao;
  periodos: Periodo[];
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const { pode } = useSessao();
  const [justificativa, setJustificativa] = useState('');
  const [reabrir, setReabrir] = useState('');
  const [erro, setErro] = useState('');
  const mes = p.data.slice(0, 7);
  const fechado = periodos.find((x) => x.mes === mes && x.fechado);
  // prévia do servidor: o que muda em cada obrigação (inclusive a reversão do crédito que dependia deste pagamento)
  const previa = useCarga(
    () =>
      api.post<{
        reversaoCredito: string;
        fatura?: { numero: string; antes: string; depois: string; pago: string } | null;
        bloqueio?: string | null;
        obrigacoes: { numero: string; valor: string; antes: Record<'aplicado' | 'desconto' | 'credito' | 'saldo', string>; depois: Record<'aplicado' | 'desconto' | 'credito' | 'saldo', string>; reversaoCredito: string }[];
      }>(`/api/contas-pagar/pagamentos/${p.id}/previa-estorno`, {}),
    [p.id],
  );
  const estornar = async () => {
    setErro('');
    try {
      await api.post(`/api/contas-pagar/pagamentos/${p.id}/estornar`, {
        justificativa,
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  const pedirReabertura = async () => {
    setErro('');
    try {
      await api.post(`/api/financeiro/periodos/${mes}/reabrir`, {
        justificativa: reabrir,
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  if (fechado)
    return (
      <Modal
        titulo={`Estornar pagamento — ${o.numero}`}
        aoFechar={aoFechar}
        medio
        rodape={
          <>
            <button type="button" className="botao-secundario" onClick={aoFechar}>
              Voltar
            </button>
            {pode('reabrir', 'periodo_financeiro') && (
              <button type="button" className="botao-primario" onClick={() => void pedirReabertura()} data-teste="reabrir-periodo">
                Reabrir {mesExtenso(mes)}
              </button>
            )}
          </>
        }
      >
        <Estado
          tipo="bloqueado"
          titulo={`${mesExtenso(mes)} está fechado`}
          teste="estorno-bloqueado"
          itens={[
            `Fechado em ${dataBr(fechado.ultimoEvento.em)}${fechado.ultimoEvento.por ? ` por ${fechado.ultimoEvento.por}` : ''}.`,
            `Pagamento de ${dataBr(p.data)} · ${rs(p.valor)} · ${o.descricao}: não pode ser estornado nem alterado.`,
          ]}
        />
        {pode('reabrir', 'periodo_financeiro') ? (
          <Campo rotulo="Justificativa da reabertura" ajuda="Fica na auditoria. Depois de reabrir, corrija o próprio registro (estorno, novo pagamento ou documento) — sem nova despesa e sem mudar a competência — e feche o mês de novo.">
            {(id) => <input id={id} value={reabrir} onChange={(e) => setReabrir(e.target.value)} maxLength={500} data-teste="justificativa-reabertura" />}
          </Campo>
        ) : (
          <p className="texto-secundario">Peça a reabertura a quem tem a permissão “Reabrir período financeiro”.</p>
        )}
        {erro && (
          <p className="aviso aviso-erro" role="alert">
            {erro}
          </p>
        )}
      </Modal>
    );
  return (
    <Modal
      titulo="Estornar pagamento"
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!previa.dados || Boolean(previa.dados.bloqueio)} title={previa.dados?.bloqueio ?? undefined} onClick={() => void estornar()} data-teste="confirmar-estorno">
            Confirmar estorno
          </button>
        </>
      }
    >
      <p className="faixa-resumo-modal">
        <span>
          Pagamento de <strong>{dataBr(p.data)}</strong>
        </span>
        <span>
          <strong>{rs(p.valor)}</strong>
        </span>
        <span>{MEIOS[p.meio] ?? p.meio}</span>
      </p>
      <Campo rotulo="Justificativa">
        {(id) => <textarea id={id} rows={2} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={500} data-teste="justificativa-estorno" />}
      </Campo>
      {previa.dados?.bloqueio && (
        <div className="aviso aviso-atencao-fin" role="status" data-teste="estorno-bloqueio">
          <strong>Estorno bloqueado.</strong> {previa.dados.bloqueio}
        </div>
      )}
      {previa.dados?.fatura && (
        <p className="faixa-resumo-modal" data-teste="estorno-fatura">
          <span>
            Fatura <strong>{previa.dados.fatura.numero}</strong>
          </span>
          <span>
            cobrado {rs(previa.dados.fatura.antes)} → <strong>{rs(previa.dados.fatura.depois)}</strong>
          </span>
          <span>pago {rs(previa.dados.fatura.pago)}</span>
        </p>
      )}
      <section className="quadro-depois" data-teste="apos-estorno">
        <h3>Após o estorno</h3>
        {previa.dados ? (
          <div className="rolagem-tabela-previa">
            <table className="tabela tabela-compacta" data-teste="previa-estorno">
              <thead>
                <tr>
                  <th>Obrigação</th>
                  <th className="numero">Valor</th>
                  <th className="numero">Aplicado</th>
                  <th className="numero">Desconto</th>
                  <th className="numero">Crédito a recuperar</th>
                  <th className="numero">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {previa.dados.obrigacoes.map((x) => (
                  <tr key={x.numero}>
                    <td>{x.numero}</td>
                    <td className="numero">{rs(x.valor)}</td>
                    {(['aplicado', 'desconto', 'credito', 'saldo'] as const).map((k) => (
                      <td key={k} className="numero">
                        {x.antes[k] === x.depois[k] ? rs(x.depois[k]) : `${rs(x.antes[k])} → ${rs(x.depois[k])}`}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EstadoCarga carregando={previa.carregando} erro={previa.erro} recarregar={() => void previa.recarregar()} />
        )}
        {previa.dados && Number(previa.dados.reversaoCredito) > 0 && (
          <div className="aviso aviso-atencao-fin" role="status" data-teste="estorno-reverte-credito">
            <strong>◐ Este pagamento é a origem de crédito a recuperar:</strong> o estorno registra, junto, a reversão de {rs(previa.dados.reversaoCredito)} desse crédito (vinculada ao estorno, com a mesma justificativa). Nada é apagado.
          </div>
        )}
        <small className="texto-secundario">O pagamento original continua visível como estornado, com autor, data e justificativa.</small>
      </section>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-estorno">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ---------- Anexar comprovante ---------- */
function AnexarComprovante({ pagamento: p, aoFechar, aoSalvar }: { pagamento: Pagamento; aoFechar: () => void; aoSalvar: () => void }) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState('');
  const salvar = async () => {
    setErro('');
    try {
      await api.post(`/api/contas-pagar/pagamentos/${p.id}/anexos`, {
        arquivo: await arquivoParaApi(arquivo),
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Comprovante — ${p.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!arquivo} onClick={() => void salvar()} data-teste="salvar-comprovante">
            Anexar
          </button>
        </>
      }
    >
      <p className="texto-secundario">O comprovante fica no pagamento, não no documento da despesa.</p>
      <Arquivo rotulo="Comprovante de pagamento" arquivo={arquivo} setArquivo={setArquivo} teste="arquivo-comprovante" />
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ---------- Cancelar despesa ---------- */
function CancelarDespesa({ ficha: f, aoFechar, aoSalvar }: { ficha: Ficha; aoFechar: () => void; aoSalvar: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const salvar = async () => {
    setErro('');
    try {
      await api.post(`/api/contas-pagar/despesas/${f.despesa.id}/cancelar`, {
        motivo,
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Cancelar despesa — ${f.despesa.numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={() => void salvar()} data-teste="confirmar-cancelamento">
            Cancelar despesa
          </button>
        </>
      }
    >
      <p>{f.despesa.tipo === 'parcelada' ? `As ${f.parcelas.length} parcelas ficam canceladas` : 'A obrigação fica cancelada'} e continuam visíveis no histórico. Só é possível sem pagamento ativo.</p>
      <Campo rotulo="Motivo">{(id) => <input id={id} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} data-teste="motivo-cancelamento" />}</Campo>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ---------- Editar (antes do primeiro pagamento) ---------- */
function EditarDespesa({ ficha: f, aoFechar, aoSalvar }: { ficha: Ficha; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const d = f.despesa;
  const o = f.obrigacao;
  const comPagamento = f.pagamentos.some((p) => !p.estorno);
  const [descricao, setDescricao] = useState(d.descricao);
  const [competencia, setCompetencia] = useState(mesBr(d.competencia));
  const [valor, setValor] = useState(deCentavos(Math.round(Number(o.valor) * 100)));
  const [vencimento, setVencimento] = useState(o.vencimento);
  const [contaId, setContaId] = useState(d.planoContasId);
  const [centroId, setCentroId] = useState(d.centroCustoId ?? '');
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const a = apoio.dados;
  const salvar = async () => {
    setErro('');
    try {
      await api.patch(`/api/contas-pagar/despesas/${d.id}`, {
        descricao,
        ...(contaId !== d.planoContasId || (centroId || null) !== d.centroCustoId ? { plano_contas_id: contaId, centro_custo_id: centroId || null } : {}),
        ...(comPagamento ? { justificativa } : { competencia, ...(d.tipo === 'avulsa' ? { valor, vencimento } : {}) }),
        atualizado_em: d.atualizadoEm,
      });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Editar despesa — ${d.numero}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={comPagamento && justificativa.trim().length < 4} onClick={() => void salvar()} data-teste="salvar-edicao">
            Salvar
          </button>
        </>
      }
    >
      <p className="texto-secundario">
        {comPagamento
          ? 'A despesa já tem pagamento: corrija descrição e classificação no próprio registro, com justificativa. Competência, favorecido, valor e vencimento não mudam aqui (valor do documento: “Completar documento”; pagamento: estorno).'
          : `Antes do primeiro pagamento. ${d.tipo === 'parcelada' ? 'Na parcelada, valor e vencimentos não são reescritos: cancele e lance de novo.' : ''}`}
      </p>
      {!a ? (
        <EstadoCarga carregando={apoio.carregando} erro={apoio.erro} recarregar={apoio.recarregar} />
      ) : (
        <div className="grade-form duas">
          <Campo rotulo="Descrição">{(id) => <input id={id} value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={200} data-teste="editar-descricao" />}</Campo>
          {comPagamento ? (
            <Campo rotulo="Competência">{(id) => <input id={id} value={mesBr(d.competencia)} readOnly className="somente-leitura" />}</Campo>
          ) : (
            <Campo rotulo="Competência">{(id) => <input id={id} value={competencia} onChange={(e) => setCompetencia(e.target.value)} maxLength={7} placeholder="MM/AAAA" />}</Campo>
          )}
          <Campo rotulo="Conta gerencial">
            {(id) => (
              <select id={id} value={contaId} onChange={(e) => setContaId(e.target.value)} data-teste="editar-conta">
                {!a.contasObrigacao.some((c) => c.id === contaId) && (
                  <option value={contaId}>
                    {o.conta.codigo} — {o.conta.nome}
                  </option>
                )}
                {a.contasObrigacao.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.codigo} — {c.nome}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          <Campo rotulo="Centro de custo">
            {(id) => (
              <select id={id} value={centroId} onChange={(e) => setCentroId(e.target.value)} data-teste="editar-centro">
                <option value="">Sem centro de custo</option>
                {a.centros.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          {!comPagamento && d.tipo === 'avulsa' && (
            <>
              <Campo rotulo="Valor">{(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} inputMode="decimal" />}</Campo>
              <CampoData rotulo="Vencimento" valor={vencimento} aoMudar={setVencimento} />
            </>
          )}
          {comPagamento && (
            <div className="largura-total">
              <Campo rotulo="Justificativa">{(id) => <input id={id} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={500} data-teste="editar-justificativa" />}</Campo>
            </div>
          )}
        </div>
      )}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ---------- Retirar anexo (o histórico da vinculação e da retirada fica na ficha e na auditoria) ---------- */
function RetirarAnexo({ anexo: x, aoFechar, aoSalvar }: { anexo: Anexo; aoFechar: () => void; aoSalvar: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const salvar = async () => {
    setErro('');
    try {
      await api.post(`/api/contas-pagar/anexos/${x.id}/retirar`, { motivo });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  return (
    <Modal
      titulo="Retirar arquivo"
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={motivo.trim().length < 4} onClick={() => void salvar()} data-teste="confirmar-retirada">
            Retirar arquivo
          </button>
        </>
      }
    >
      <p className="faixa-resumo-modal">
        <span>
          <strong>{x.nome_original}</strong>
        </span>
        <span>{x.papel === 'documento' ? 'documento da despesa' : 'comprovante do pagamento'}</span>
        <span>
          enviado em {dataBr(x.criado_em)}
          {x.enviado_por ? ` por ${x.enviado_por}` : ''}
        </span>
      </p>
      <p className="texto-secundario">O arquivo deixa de valer, mas não é apagado: a ficha mostra quem enviou, quem retirou, quando e por quê.</p>
      <Campo rotulo="Motivo da retirada">{(id) => <input id={id} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} data-teste="motivo-retirada" />}</Campo>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ====================================================================== Períodos financeiros (fechar / reabrir; C8) */
/** E1 (0055): posição de uma aplicação no último dia do mês, como o registro do fechamento guarda */
interface RetratoAplicacao {
  aplicacao: string;
  descricao: string;
  conta: string;
  principal: string;
  provisoria: boolean;
  faltaDeduzir: { evento: string; liquido: string }[];
  ultimaInformada: { evento: string; data: string; principal: string; diferenca: string; ciencia: boolean } | null;
  pendencias: { tipo: 'composicao_a_informar' | 'posicao_divergente' | 'aguardando_extrato'; evento: string; valor: string }[];
}
const ROTULO_PEND_APLICACAO = { composicao_a_informar: 'composição a informar', posicao_divergente: 'posição divergente', aguardando_extrato: 'aguardando extrato' } as const;
function RetratoAplicacoes({ itens, teste }: { itens: RetratoAplicacao[]; teste: string }) {
  return (
    <ul className="conc-efeitos" data-teste={teste}>
      {itens.map((a) => (
        <li key={a.aplicacao} data-teste={`${teste}-${a.aplicacao}`}>
          <ComCodigos texto={a.aplicacao} /> · {a.descricao} · principal {rs(a.principal)}
          {a.provisoria ? ` (provisório: falta deduzir ${a.faltaDeduzir.map((f) => f.evento).join(', ')})` : ''}
          {a.pendencias.length > 0 ? ` — ${a.pendencias.map((p) => `${ROTULO_PEND_APLICACAO[p.tipo]} ${p.evento}`).join(' · ')}` : ' — sem pendências'}
        </li>
      ))}
    </ul>
  );
}
interface HistoricoPeriodoItem { acao: 'fechar' | 'reabrir'; numero: number; em: string; por: string | null; justificativa: string; pendencias: number | null; aplicacoes: RetratoAplicacao[] | null; semRetrato?: boolean }
function HistoricoPeriodo({ mes, versao }: { mes: string; versao: number }) {
  const h = useCarga(() => api.get<HistoricoPeriodoItem[]>(`/api/financeiro/periodos/${mes}/historico`), [mes, versao]);
  if (!h.dados?.length) return null;
  return (
    <details className="fech-detalhe" data-teste="historico-periodo">
      <summary>Histórico de {mesExtenso(mes)} ({plural(h.dados.filter((x) => x.acao === 'fechar').length, 'fechamento', 'fechamentos')}, preservados)</summary>
      <ul className="conc-efeitos">
        {h.dados.map((x, i) => (
          <li key={i} data-teste={`historico-${x.acao}-${x.numero}`}>
            <strong>{x.acao === 'fechar' ? `Fechamento nº ${x.numero}` : `Reabertura nº ${x.numero}`}</strong> · {new Date(x.em).toLocaleString('pt-BR')}
            {x.por ? ` · ${x.por}` : ''} — “{x.justificativa}”{x.acao === 'fechar' && x.pendencias ? ` · ${plural(x.pendencias, 'pendência', 'pendências')} de conciliação` : ''}
            {x.semRetrato && (
              <div className="texto-secundario conc-mini" data-teste={`sem-retrato-${x.numero}`}>
                sem retrato das aplicações: fechado por uma versão anterior, que não grava as posições — reabra e feche de novo para registrá-las
              </div>
            )}
            {x.aplicacoes && x.aplicacoes.length > 0 && <RetratoAplicacoes itens={x.aplicacoes} teste={`retrato-${x.numero}`} />}
          </li>
        ))}
      </ul>
    </details>
  );
}
interface PendenciasPeriodo {
  mes: string;
  aplicacoes: RetratoAplicacao[];
  contas: {
    conta: { banco: string; agencia: string; numero: string };
    saidas: number;
    entradas: number;
    aplicacoes: number;
    transferencias: number;
    pagamentosSemConciliacao: number;
    /** 0051 */
    paresProvaveis: number;
    recebimentosAguardando: number;
    recuperacoesAguardando: number;
    devolucoesCreditoAguardando: number;
    devolucoesPendentesVinculo: number;
    ambiguos: number;
    detalhe: { tipo: string; item: string; data: string; valor: string; observacao: string }[];
    total: number;
    conferencia: { data: string; extrato: string; calculado: string; confere: boolean; diferenca: string } | null;
  }[];
  totais: { saidas: number; entradas: number; aplicacoes: number; transferencias: number; pagamentosSemConciliacao: number };
  total: number;
}
const situacaoPeriodo = (p?: Periodo) =>
  !p || !p.fechado ? { texto: 'Aberto', tom: 'aberto' as const } : (p.pendencias ?? 0) > 0 ? { texto: `Fechado com pendências (${p.pendencias})`, tom: 'parcial' as const } : { texto: 'Fechado', tom: 'encerrado' as const };

function PeriodosFinanceiros({ aoFechar }: { aoFechar: () => void }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<Periodo[]>('/api/financeiro/periodos'), []);
  const hoje = hojeIso();
  const [mes, setMes] = useState(somarMeses(`${hoje.slice(0, 7)}-01`, -1).slice(0, 7));
  const [justificativa, setJustificativa] = useState('');
  const [confirmar, setConfirmar] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  // E0 (M3): do início do histórico financeiro (calculado no servidor; nada fixo no código) até o mês atual
  const inicio = useCarga(() => api.get<{ global: string | null }>('/api/financeiro/periodos/inicio'), []);
  const atual = hoje.slice(0, 7);
  const primeiro = inicio.dados?.global && inicio.dados.global < atual ? inicio.dados.global : atual;
  const meses: string[] = [];
  for (let i = 0; somarMeses(`${atual}-01`, -i).slice(0, 7) >= primeiro && i < 600; i++) meses.push(somarMeses(`${atual}-01`, -i).slice(0, 7));
  for (const p of carga.dados ?? []) if (!meses.includes(p.mes)) meses.push(p.mes);
  meses.sort().reverse();
  // o mês sugerido (o anterior) pode ser anterior ao histórico: aí começa no primeiro mês listado
  useEffect(() => {
    if (inicio.dados && !meses.includes(mes)) setMes(meses[meses.length - 1] ?? atual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inicio.dados]);
  const estado = (m: string) => carga.dados?.find((p) => p.mes === m);
  const fechado = Boolean(estado(mes)?.fechado);
  const podeFechar = pode('fechar', 'periodo_financeiro');
  // B2: pendências de conciliação de TODAS as contas do mês (o filtro de conta das telas não se aplica)
  const pend = useCarga(() => (!fechado && podeFechar ? api.get<PendenciasPeriodo>(`/api/financeiro/periodos/${mes}/pendencias`) : Promise.resolve(null)), [mes, fechado, podeFechar]);
  const p = pend.dados;
  const comPendencias = Boolean(p && p.total > 0);
  useEffect(() => setConfirmar(false), [mes]);
  const [versaoHistorico, setVersaoHistorico] = useState(0);
  const agir = async () => {
    setErro('');
    setOk('');
    try {
      const r = await api.post<{ situacao?: string; pendencias?: number }>(`/api/financeiro/periodos/${mes}/${fechado ? 'reabrir' : 'fechar'}`, { justificativa, ...(comPendencias ? { confirmar_pendencias: confirmar } : {}) });
      setOk(fechado ? `${mesExtenso(mes)} reaberto.` : r.situacao === 'fechado_com_pendencias' ? `${mesExtenso(mes)} fechado com pendências (${r.pendencias}).` : `${mesExtenso(mes)} fechado.`);
      setJustificativa('');
      setConfirmar(false);
      setVersaoHistorico((v) => v + 1);
      void carga.recarregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : (e as Error).message);
    }
  };
  const permitido = fechado ? pode('reabrir', 'periodo_financeiro') : podeFechar && (!comPendencias || confirmar) && justificativa.trim().length >= 4;
  const rotuloBotao = fechado ? `Reabrir ${mesExtenso(mes)}` : comPendencias ? 'Fechar com pendências' : `Fechar ${mesExtenso(mes)}`;
  return (
    <Modal
      titulo={fechado ? 'Períodos financeiros' : `Períodos financeiros — fechar ${mesExtenso(mes)}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!permitido} onClick={() => void agir()} data-teste="agir-periodo">
            {rotuloBotao}
          </button>
        </>
      }
    >
      <p className="texto-secundario">Mês fechado: lançamentos, pagamentos, estornos, conciliações e desfazer daquele mês não mudam (importar o extrato continua possível). Fechar e reabrir exigem justificativa e ficam na auditoria.</p>
      <div className="grade-form duas">
        <Campo rotulo="Mês">
          {(id) => (
            <select id={id} value={mes} onChange={(e) => setMes(e.target.value)} data-teste="mes-periodo">
              {meses.map((m) => (
                <option key={m} value={m}>
                  {mesBr(m)} · {situacaoPeriodo(estado(m)).texto.toLowerCase()}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <div className="campo">
          <span className="rotulo-campo">Situação</span>
          <span data-teste="situacao-periodo">
            <SeloSituacao texto={situacaoPeriodo(estado(mes)).texto} tom={situacaoPeriodo(estado(mes)).tom} />
          </span>
        </div>
      </div>
      {!fechado && podeFechar && (
        <>
          {!p ? (
            <EstadoCarga carregando={pend.carregando} erro={pend.erro} recarregar={pend.recarregar} teste="pendencias-periodo" />
          ) : (
            <div data-teste="pendencias-periodo">
              <p className={`aviso ${comPendencias ? 'aviso-atencao-fin' : 'aviso-sucesso'}`}>
                {comPendencias ? (
                  <>
                    <strong>Conciliação de {mesExtenso(mes)} com pendências</strong> em <strong>todas as contas</strong> do período (o filtro de conta das telas não se aplica):
                  </>
                ) : (
                  <>Nenhuma pendência de conciliação em {mesExtenso(mes)}.</>
                )}
                <Ajuda titulo="Pendências de conciliação">
                  Movimentos do extrato ainda não conciliados (saídas, entradas a identificar, aplicações/resgates e transferências) e pagamentos pela conta ainda sem vínculo com o extrato. A conferência do saldo é só informação. Fechar com pendências não é conciliação concluída: o mês fica identificado como “Fechado com pendências”.
                </Ajuda>
              </p>
              {comPendencias && (
                // E3-08: uma linha por tipo de pendência (sem colunas escondidas em 1440 e 1024); par provável conta uma vez
                <div className="fech-contas" data-teste="tabela-pendencias-periodo">
                  {p.contas.map((c) => {
                    const valorDe = (tipo: string) => {
                      const xs = c.detalhe.filter((x) => x.tipo === tipo);
                      return xs.length ? rs(xs.reduce((s2, x) => s2 + Number(x.valor), 0)) : '—';
                    };
                    const linhas: [string, string, number, string, string][] = [
                      ['par', 'Pares prováveis (sugestões)', c.paresProvaveis, valorDe('par_provavel'), 'entrada × recebimento/recuperação únicos dos dois lados: 1 par = 1 pendência; cada item em um só par'],
                      ['entradas', 'Entradas a identificar', c.entradas, valorDe('entrada_a_identificar'), `sem par${c.ambiguos ? ' (inclui as ambíguas)' : ''}`],
                      ['recebimentos', 'Recebimentos aguardando extrato', c.recebimentosAguardando, valorDe('recebimento_aguardando_extrato'), 'data efetiva no mês, sem par (ou par em outro mês)'],
                      ['recuperacoes', 'Recuperações aguardando extrato', c.recuperacoesAguardando, valorDe('recuperacao_aguardando_extrato'), 'Contas a Pagar: devolução em dinheiro registrada, sem extrato'],
                      ['devolucoes-credito', 'Devoluções de crédito do pagador aguardando extrato', c.devolucoesCreditoAguardando, valorDe('devolucao_credito_aguardando_extrato'), 'saída registrada no Receber, sem extrato'],
                      ['pendentes-vinculo', 'Devoluções de fornecedor pendentes de vínculo', c.devolucoesPendentesVinculo, valorDe('devolucao_pendente_vinculo'), 'natureza registrada, sem crédito a recuperar vinculado'],
                      ['saidas', 'Saídas sem conciliação', c.saidas, '—', 'como hoje'],
                      ['pagamentos', 'Pagamentos sem conciliação', c.pagamentosSemConciliacao, '—', 'como hoje'],
                      ['aplicacoes', 'Aplicações/resgates', c.aplicacoes, '—', 'frente separada (RDB)'],
                      ['transferencias', 'Transferências', c.transferencias, '—', 'só com as duas pontas'],
                    ];
                    return (
                      <div key={`${c.conta.banco}${c.conta.numero}`} className="fech-conta">
                        <div className="fech-conta-topo">
                          <span className="periodo-conta" data-teste={`conta-periodo-${c.conta.numero}`}>
                            <strong>{c.conta.banco}</strong>{' '}
                            <span className="texto-secundario">
                              ag. {c.conta.agencia} · conta {c.conta.numero}
                            </span>
                          </span>
                          <span>
                            {!c.conferencia ? (
                              <span className="texto-secundario">saldo: sem saldo do extrato no mês</span>
                            ) : c.conferencia.confere ? (
                              <span className="conc-ok">saldo confere ✓</span>
                            ) : (
                              <span className="conc-dif">
                                extrato {rs(c.conferencia.extrato)} × calculado {rs(c.conferencia.calculado)} · diferença {rs(c.conferencia.diferenca)}
                              </span>
                            )}
                          </span>
                        </div>
                        <table className="tabela tabela-compacta fech-tabela" data-teste={`pendencias-conta-${c.conta.numero}`}>
                          <thead>
                            <tr>
                              <th>Pendência</th>
                              <th className="numero">Qtd</th>
                              <th className="numero">Valor</th>
                              <th>Como é contada</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linhas
                              .filter(([, , qtd]) => qtd > 0)
                              .map(([chave, rotulo, qtd, valor, como]) => (
                                <tr key={chave} data-teste={`pendencia-${c.conta.numero}-${chave}`}>
                                  <td>{rotulo}</td>
                                  <td className="numero">{qtd}</td>
                                  <td className="numero">{valor}</td>
                                  <td className="texto-secundario">{como}</td>
                                </tr>
                              ))}
                            <tr data-teste={`pendencia-${c.conta.numero}-total`}>
                              <td>
                                <strong>Total da conta</strong>
                              </td>
                              <td className="numero">
                                <strong>{c.total}</strong>
                              </td>
                              <td className="numero">—</td>
                              <td className="texto-secundario">valores não se somam entre lados (banco × HUB)</td>
                            </tr>
                          </tbody>
                        </table>
                        {c.detalhe.length > 0 && (
                          <details className="fech-detalhe" data-teste={`detalhe-pendencias-${c.conta.numero}`}>
                            <summary>Ver itens ({c.detalhe.length})</summary>
                            <ul className="conc-efeitos">
                              {c.detalhe.map((x, k) => (
                                <li key={k}>
                                  {dataBr(x.data)} · <ComCodigos texto={x.item} /> · {rs(x.valor)} — <ComCodigos texto={x.observacao} />
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    );
                  })}
                  <p className="texto-secundario">
                    <strong>Total: {plural(p.total, 'pendência', 'pendências')}</strong> em todas as contas.
                  </p>
                </div>
              )}
              {p.aplicacoes.length > 0 && (
                <div className="aviso aviso-atencao-fin" data-teste="aplicacoes-periodo">
                  <strong>Aplicações em {dataBr(`${mes}-${String(new Date(Date.UTC(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)), 0)).getUTCDate())}`)}</strong>
                  {(() => {
                    const n = p.aplicacoes.reduce((s2, a) => s2 + a.pendencias.filter((x) => x.tipo !== 'aguardando_extrato').length, 0);
                    return n ? (
                      <span data-teste="aviso-pendencias-aplicacao">
                        {' '}
                        — <strong>o mês pode ser fechado com {plural(n, 'pendência de aplicação', 'pendências de aplicação')}</strong> (composição a informar ou posição divergente). A posição provisória, a divergência e as pendências ficam registradas no fechamento. Completar ou corrigir esses valores depois exige reabrir o período e fechar de novo (o fechamento anterior fica preservado).
                      </span>
                    ) : (
                      <> — o registro do fechamento guarda esta posição. Incluir ou alterar composição de um mês fechado exige reabrir e fechar de novo.</>
                    );
                  })()}
                  <RetratoAplicacoes itens={p.aplicacoes} teste="retrato-previa" />
                </div>
              )}
              {comPendencias && (
                <label className="conc-confirmar conc-confirmar-destaque" data-teste="confirmar-pendencias">
                  <input type="checkbox" checked={confirmar} onChange={(e) => setConfirmar(e.target.checked)} />
                  <span>
                    <strong>Fechar {mesExtenso(mes)} com {plural(p.total, 'pendência', 'pendências')} de conciliação</strong>
                    <br />
                    <span className="texto-secundario">O período ficará identificado como “Fechado com pendências ({p.total})”.</span>
                  </span>
                </label>
              )}
            </div>
          )}
        </>
      )}
      <Campo rotulo="Justificativa *">{(id) => <textarea id={id} rows={2} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={500} data-teste="justificativa-periodo" />}</Campo>
      {estado(mes) && (
        <p className="texto-secundario" data-teste="ultimo-evento-periodo">
          Último evento: {estado(mes)!.ultimoEvento.acao === 'fechar' ? 'fechado' : 'reaberto'} em {dataBr(estado(mes)!.ultimoEvento.em)}
          {estado(mes)!.ultimoEvento.por ? ` por ${estado(mes)!.ultimoEvento.por}` : ''} — {estado(mes)!.ultimoEvento.justificativa}
        </p>
      )}
      <HistoricoPeriodo mes={mes} versao={versaoHistorico} />
      {ok && <p className="aviso aviso-sucesso">{ok}</p>}
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ---------- 0051: recuperação em dinheiro do crédito a recuperar (E3-10) ---------- */
function RegistrarRecuperacao({ obrigacao, saldo, aoFechar, aoSalvar }: { obrigacao: string; saldo: string; aoFechar: () => void; aoSalvar: () => void }) {
  const apoio = useApoio();
  const [conta, setConta] = useState('');
  const [data, setData] = useState(hojeIso());
  const [valor, setValor] = useState(String(saldo).replace('.', ','));
  const [observacao, setObservacao] = useState('');
  const [chave] = useState(novaChave);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const acima = centavosDe(valor) > Math.round(Number(saldo) * 100);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post(`/api/contas-pagar/obrigacoes/${obrigacao}/recuperacoes`, { conta_bancaria_id: conta, data_efetiva: data, valor, observacao: observacao || undefined, chave_idempotencia: chave });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar recuperação recebida — ${obrigacao}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!conta || !data || centavosDe(valor) <= 0 || acima || enviando} onClick={() => void enviar()} data-teste="confirmar-recuperacao">
            Registrar recuperação
          </button>
        </>
      }
    >
      <div data-teste="modal-recuperacao">
        <p className="texto-secundario">
          Devolução em dinheiro do fornecedor: reduz o saldo a recuperar ({rs(saldo)}) e fica <strong>aguardando extrato</strong> até a conciliação com a entrada do banco. O pagamento original não muda; nunca acima do saldo.
        </p>
        <div className="grade-form dois">
          <Campo rotulo="Conta que recebeu *">
            {(id) => (
              <select id={id} value={conta} onChange={(e) => setConta(e.target.value)} data-teste="conta-recuperacao">
                <option value="">Escolha…</option>
                {(apoio.dados?.bancos ?? []).filter((b) => b.status === 'ativo').map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.banco} · ag. {b.agencia} · {b.numero}
                  </option>
                ))}
              </select>
            )}
          </Campo>
          <CampoData rotulo="Data efetiva *" valor={data} aoMudar={setData} max={hojeIso()} teste="data-recuperacao" />
          <Campo rotulo="Valor recuperado *">{(id) => <input id={id} value={valor} onChange={(e) => setValor(e.target.value)} data-teste="valor-recuperacao" />}</Campo>
          <Campo rotulo="Observação (opcional)">{(id) => <input id={id} maxLength={500} value={observacao} onChange={(e) => setObservacao(e.target.value)} />}</Campo>
        </div>
        {acima && <p className="aviso aviso-erro">O valor passa do saldo a recuperar ({rs(saldo)}).</p>}
        {erro && (
          <p className="aviso aviso-erro" role="alert" data-teste="erro-recuperacao">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}

function EstornarRecuperacao({ numero, aoFechar, aoSalvar }: { numero: string; aoFechar: () => void; aoSalvar: () => void }) {
  const [just, setJust] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post(`/api/contas-pagar/recuperacoes/${numero}/estornar`, { justificativa: just });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Estornar recuperação ${numero}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={just.trim().length < 10 || enviando} onClick={() => void enviar()} data-teste="confirmar-estornar-rcp">
            Estornar registro
          </button>
        </>
      }
    >
      <p className="texto-secundario">Corrige um registro incorreto: a recuperação deixa de contar e o saldo a recuperar volta. Não é devolução de dinheiro. Recuperação conciliada exige desfazer a conciliação antes.</p>
      <Campo rotulo="Justificativa *">{(id) => <textarea id={id} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} placeholder="Ao menos 10 caracteres" data-teste="justificativa-estornar-rcp" />}</Campo>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-estornar-rcp">
          {erro}
        </p>
      )}
    </Modal>
  );
}
